export type Lang = "fr" | "ar" | "zgh";

export const langConfig: Record<Lang, { label: string; dir: "ltr" | "rtl"; fontClass: string }> = {
  fr: { label: "Français", dir: "ltr", fontClass: "font-body" },
  ar: { label: "العربية", dir: "rtl", fontClass: "font-arabic" },
  zgh: { label: "ⵜⴰⵎⴰⵣⵉⵖⵜ", dir: "ltr", fontClass: "font-amazigh" },
};

type ActionItem = {
  title: string; description: string; cta: string; slug: string;
  content: string[]; links: { label: string; url: string }[];
};

type Translations = {
  nav: { home: string; why: string; parties: string; resources: string; engage: string };
  hero: {
    badge: string; title1: string; titleHighlight: string; title2: string; subtitle: string;
    voteBtn: string; engageBtn: string; observeBtn: string; candidatBtn: string;
    stats: { value: string; label: string }[];
  };
  why: {
    tag: string; title1: string; titleHighlight: string; description: string;
    reasons: { title: string; description: string }[];
    pageTitle: string; pageSubtitle: string;
    detailedReasons: { title: string; description: string; details: string[] }[];
  };
  parties: {
    tag: string; title1: string; titleHighlight: string; description: string;
    seats: string; viewProgram: string; visitWebsite: string;
    otherPartiesTitle: string; otherPartiesDescription: string;
    nonParliamentaryTitle: string; nonParliamentaryDescription: string;
    partiesFocus: Record<string, string>; partiesNames: Record<string, string>;
  };
  actions: {
    tag: string; title1: string; titleHighlight: string; description: string;
    usefulLinks: string;
    items: ActionItem[];
  };
  resources: {
    tag: string; title1: string; titleHighlight: string; description: string;
    items: { title: string; description: string; cta: string; slug: string; links: { label: string; url: string }[]; content: string[] }[];
  };
  cta: { title1: string; titleHighlight: string; description: string; btn1: string; btn2: string };
  footer: { description: string; usefulLinks: string; links: string[]; contact: string; copyright: string };
  voter: {
    title: string; subtitle: string;
    checkTitle: string; checkDescription: string; checkBtn: string;
    findTitle: string; findDescription: string;
    region: string; province: string; commune: string; bureau: string; searchBtn: string;
  };
  engagement: {
    title: string; subtitle: string;
    party: string; selectParty: string; object: string; firstName: string; lastName: string; genre: string; selectGenre: string; genderOptions: { male: string; female: string };
    age: string; selectAge: string; ageOptions: { "18-24": string; "25-34": string; "35-44": string; "45-54": string; "55-64": string; "65+": string; };
    profession: string; selectProfession: string;
    region: string; province: string; commune: string;
    phone: string; email: string; message: string;
    declaration: string; cndpDisclaimer: string; submit: string;
    successTitle: string; successMessage: string;
    errors: { required: string; email: string; phone: string };
  };
  common: { backHome: string; loading: string };
  candidacy: {
    title: string; titleHighlight: string; subtitle: string;
    tabGeneral: string; tabYouth: string;
    eligibilityTitle: string;
    eligibilityItems: { title: string; desc: string }[];
    financeTitle: string; subventionsTitle: string; subventionsDesc: string; subventionsItems: string[];
    plafondsTitle: string; plafondsDesc: string; plafondsItems: string[];
    timelineTitle: string;
    timelineItems: { title: string; desc: string }[];
    incompTitle: string; incompDesc: string; incompNote: string; incompItems: string[];
    ctaTitle: string; ctaDesc: string; btnParti: string; btnAgenda: string; btnHome: string;
    youth: {
      badge: string;
      title: string;
      desc: string;
      eligibilityTitle: string;
      eligibilityItems: string[];
      financeTitle: string;
      subventionsTitle: string;
      subventionsDesc: string;
      rulesTitle: string;
      rulesItems: string[];
    };
  };
  observateur: {
    heroTitle: string; heroBadge: string; heroDesc: string;
    section1Title: string; institutions: { title: string; desc: string }[];
    section2Title: string; section2Desc: string;
    conditionsTitle: string; conditions: string[];
    modalitesTitle: string; modalites: string[];
    section3Title: string;
    droitsTitle: string; droits: string[];
    obligationsTitle: string; obligations: string[];
    cndhTitle: string; cndhBtn: string;
    downloadTitle: string; downloadDesc: string;
    suggestedTitle: string; sugVoterTitle: string; sugVoterDesc: string; sugQuizTitle: string; sugQuizDesc: string; sugPartiTitle: string; sugPartiDesc: string;
  };
  expectations: {
    badge: string; title: string; subtitle: string; respondents: string; btnSpeak: string; updatedAt: string;
    filters: { all: string; citizen: string; young: string; women: string; ong: string; };
    kpiRespondents: string; kpiUnique: string; kpiTop: string; kpiTop3: string;
    emptyData: string; cloudTitle: string; cloudNote: string; barTitle: string; mentions: string; frequency: string;
    tableTitle: string; colRank: string; colPriority: string; colMentions: string; colPct: string; colPopularity: string; tableNote: string; btnLess: string;
    modalTitle: string; modalSubtitle: string; priority1: string; priority2: string; maxWords: string; placeholder1: string; placeholder2: string; reminder: string; ethicsText: string; btnSubmit: string; successTitle: string; successDesc: string; btnClose: string;
  };
};

const allPartiesNames_fr: Record<string, string> = {
  RNI: "Rassemblement National des Indépendants",
  PAM: "Parti Authenticité et Modernité",
  PI: "Parti de l'Istiqlal",
  USFP: "Union Socialiste des Forces Populaires",
  MP: "Mouvement Populaire",
  PPS: "Parti du Progrès et du Socialisme",
  UC: "Union Constitutionnelle",
  PJD: "Parti de la Justice et du Développement",
  MDS: "Mouvement Démocratique et Social",
  FFD: "Front des Forces Démocratiques",
  UMD: "Union Marocaine pour la Démocratie",
  PAI: "Parti de l'Avant-garde et de l'Indépendance",
  PRV: "Parti de la Renaissance et de la Vertu",
  PT: "Parti Travailliste",
  PSU: "Parti Socialiste Unifié",
  PEDD: "Parti de l'Environnement et du Développement Durable",
  CNI: "Congrès National Ittihadi",
  PSD: "Parti de la Société Démocratique",
  PDI: "Parti Démocratique et de l'Indépendance",
  PLJS: "Parti de la Liberté et de la Justice Sociale",
  PRD: "Parti de la Réforme et du Développement",
  PCS: "Parti du Centre Social",
  PND: "Parti National Démocrate",
  "Al Ahd": "Parti Al Ahd Addimocrati",
  PGVM: "Parti de la Gauche Verte Marocaine",
  PML: "Parti Marocain Libéral",
  PUD: "Parti National pour l'Unité et la Démocratie",
  PE: "Parti de l'Equité",
  PVM: "Parti des Verts Marocains",
  "Neo-Democrates": "Parti des Néo-Démocrates",
  PA: "Parti de l'Action",
  "Al Amal": "Parti Al Amal",
  PFC: "Parti des Forces Citoyennes",
  PRE: "Parti du Renouveau et de l'Equité",
  PS: "Parti Socialiste",
  Annahda: "Parti Annahda",
  PGV: "Parti de la Gauche Verte",
  SAP: "Sans Appartenance Politique",
};

const allPartiesFocus_fr: Record<string, string> = {
  RNI: "Libéralisme économique, modernisation, investissement",
  PAM: "Modernisme, développement social, bonne gouvernance",
  PI: "Nationalisme, réformes sociales, identité",
  USFP: "Social-démocratie, justice sociale, droits de l'Homme",
  MP: "Monde rural, agriculture, développement territorial",
  PPS: "Progressisme, environnement, justice sociale",
  UC: "Constitutionnalisme, libéralisme modéré",
  PJD: "Référence islamique, moralisation, réforme",
  MDS: "Démocratie sociale, modernisation",
  FFD: "Forces démocratiques, liberté, progrès",
  UMD: "Union et démocratie",
  PAI: "Avant-garde, indépendance, progrès social",
  PRV: "Valeurs, renaissance, éthique politique",
  PT: "Droits des travailleurs, justice sociale",
  PSU: "Gauche socialiste, droits humains, démocratie",
  PEDD: "Environnement, développement durable, écologie",
  CNI: "Unité nationale, démocratie participative",
  PSD: "Société démocratique, modernisation",
  PDI: "Démocratie, indépendance, réformes",
  PLJS: "Liberté, justice sociale, égalité",
  PRD: "Réforme, développement, modernisation",
  PCS: "Centre, social, modération",
  PND: "National, démocrate, développement",
  "Al Ahd": "Engagement démocratique, citoyenneté",
  PGVM: "Écologie, gauche verte, justice climatique",
  PML: "Libéralisme, modernité, ouverture",
  PUD: "Unité nationale, démocratie",
  PE: "Equité, justice sociale",
  PVM: "Écologie, environnement, durabilité",
  "Neo-Democrates": "Nouvelle démocratie, renouveau politique",
  PA: "Action citoyenne, engagement",
  "Al Amal": "Espoir, développement social",
  PFC: "Citoyenneté, forces vives",
  PRE: "Renouveau, équité sociale",
  PS: "Socialisme, progrès",
  Annahda: "Renaissance, valeurs éthiques",
  PGV: "Écologie, gauche verte",
  SAP: "Indépendant, action citoyenne",
};

const allPartiesNames_ar: Record<string, string> = {
  RNI: "التجمع الوطني للأحرار",
  PAM: "حزب الأصالة والمعاصرة",
  PI: "حزب الاستقلال",
  USFP: "الاتحاد الاشتراكي للقوات الشعبية",
  MP: "الحركة الشعبية",
  PPS: "حزب التقدم والاشتراكية",
  UC: "الاتحاد الدستوري",
  PJD: "حزب العدالة والتنمية",
  MDS: "الحركة الديمقراطية الاجتماعية",
  FFD: "جبهة القوى الديمقراطية",
  UMD: "الاتحاد المغربي للديمقراطية",
  PAI: "حزب الطليعة الديمقراطي الاشتراكي",
  PRV: "حزب النهضة والفضيلة",
  PT: "حزب العمال",
  PSU: "الحزب الاشتراكي الموحد",
  PEDD: "حزب البيئة والتنمية المستدامة",
  CNI: "المؤتمر الوطني الاتحادي",
  PSD: "حزب المجتمع الديمقراطي",
  PDI: "حزب الديمقراطية والاستقلال",
  PLJS: "حزب الحرية والعدالة الاجتماعية",
  PRD: "حزب الإصلاح والتنمية",
  PCS: "حزب الوسط الاجتماعي",
  PND: "الحزب الوطني الديمقراطي",
  "Al Ahd": "حزب العهد الديمقراطي",
  PGVM: "حزب اليسار الأخضر المغربي",
  PML: "الحزب المغربي الليبرالي",
  PUD: "حزب الوحدة الوطنية والديمقراطية",
  PE: "حزب الإنصاف",
  PVM: "حزب الخضر المغاربة",
  "Neo-Democrates": "حزب الديمقراطيين الجدد",
  PA: "حزب العمل",
  "Al Amal": "حزب الأمل",
  PFC: "حزب القوات المواطنة",
  PRE: "حزب التجديد والإنصاف",
  PS: "الحزب الاشتراكي",
  Annahda: "حزب النهضة",
  PGV: "حزب اليسار الأخضر",
  SAP: "بدون انتماء سياسي",
};

const allPartiesFocus_ar: Record<string, string> = {
  RNI: "الليبرالية الاقتصادية، التحديث، الاستثمار",
  PAM: "الحداثة، التنمية الاجتماعية، الحكامة الجيدة",
  PI: "الوطنية، الإصلاحات الاجتماعية، الهوية",
  USFP: "الاشتراكية الديمقراطية، العدالة الاجتماعية، حقوق الإنسان",
  MP: "العالم القروي، الفلاحة، التنمية الترابية",
  PPS: "التقدمية، البيئة، العدالة الاجتماعية",
  UC: "الدستورانية، الليبرالية المعتدلة",
  PJD: "المرجعية الإسلامية، التخليق، الإصلاح",
  MDS: "الديمقراطية الاجتماعية، التحديث",
  FFD: "القوى الديمقراطية، الحرية، التقدم",
  UMD: "الاتحاد والديمقراطية",
  PAI: "الطليعة، الاستقلال، التقدم الاجتماعي",
  PRV: "القيم، النهضة، الأخلاق السياسية",
  PT: "حقوق العمال، العدالة الاجتماعية",
  PSU: "اليسار الاشتراكي، حقوق الإنسان",
  PEDD: "البيئة، التنمية المستدامة",
  CNI: "الوحدة الوطنية، الديمقراطية التشاركية",
  PSD: "المجتمع الديمقراطي، التحديث",
  PDI: "الديمقراطية، الاستقلال، الإصلاحات",
  PLJS: "الحرية، العدالة الاجتماعية، المساواة",
  PRD: "الإصلاح، التنمية، التحديث",
  PCS: "الوسط، الاجتماعي، الاعتدال",
  PND: "وطني، ديمقراطي، تنمية",
  "Al Ahd": "الالتزام الديمقراطي، المواطنة",
  PGVM: "البيئة، اليسار الأخضر، العدالة المناخية",
  PML: "الليبرالية، الحداثة، الانفتاح",
  PUD: "الوحدة الوطنية، الديمقراطية",
  PE: "الإنصاف، العدالة الاجتماعية",
  PVM: "البيئة، الإيكولوجيا، الاستدامة",
  "Neo-Democrates": "ديمقراطية جديدة، تجديد سياسي",
  PA: "العمل المواطن، الالتزام",
  "Al Amal": "الأمل، التنمية الاجتماعية",
  PFC: "المواطنة، القوى الحية",
  PRE: "التجديد، الإنصاف الاجتماعي",
  PS: "الاشتراكية، التقدم",
  Annahda: "النهضة، القيم الأخلاقية",
  PGV: "البيئة، اليسار الأخضر",
  SAP: "مستقل، عمل مواطن",
};

const allPartiesNames_zgh: Record<string, string> = {
  RNI: "ⴰⵎⵢⴰⵡⴰⵙ ⴰⵏⴰⵎⵓⵔ ⵏ ⵉⵍⴻⵍⵍⵉⵢⵏ",
  PAM: "ⴰⵎⵓⵔ ⵏ ⵜⵚⵍⵉⵜ ⴷ ⵜⴰⵜⵔⴰⵔⵜ",
  PI: "ⴰⵎⵓⵔ ⵏ ⵓⵙⵉⵎⴰⵏ",
  USFP: "ⵜⴰⵎⵓⵏⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ ⵏ ⵜⵖⵔⵎⵉⵡⵉⵏ",
  MP: "ⵜⵉⵎⵓⵙⵙⵉ ⵜⴰⵏⴰⵎⵓⵔⵜ",
  PPS: "ⴰⵎⵓⵔ ⵏ ⵜⵎⴳⴳⵉⵜ ⴷ ⵜⵏⴰⵎⵓⵏⵜ",
  UC: "ⵜⴰⵎⵓⵏⵜ ⵜⴰⴷⵓⵙⵜⵓⵔⴰⵏⵜ",
  PJD: "ⴰⵎⵓⵔ ⵏ ⵜⴰⵣⵔⴼⵜ ⴷ ⵜⵏⴼⵍⵉⵜ",
  MDS: "ⵜⵉⵎⵓⵙⵙⵉ ⵜⴰⵎⴰⴳⵔⴰⵡⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ",
  FFD: "ⵜⴰⵎⴰⵜⴰⵔⵜ ⵏ ⵜⵖⵔⵎⵉⵡⵉⵏ ⵜⵉⵎⴰⴳⵔⴰⵡⵉⵏ",
  UMD: "ⵜⴰⵎⵓⵏⵜ ⵜⴰⵎⵖⵔⴰⴱⵉⵜ ⵏ ⵜⵎⴰⴳⵔⴰⵡⵜ",
  PAI: "ⴰⵎⵓⵔ ⵏ ⵜⵎⴳⴳⵉⵜ",
  PRV: "ⴰⵎⵓⵔ ⵏ ⵜⵓⴳⴳⴰⵙ ⴷ ⵜⴼⴰⴹⵉⵍⵜ",
  PT: "ⴰⵎⵓⵔ ⵏ ⵉⵎⵀⴰⵍⵏ",
  PSU: "ⴰⵎⵓⵔ ⴰⵏⴰⵎⵓⵏ ⵉⵎⵓⵏⵏ",
  PEDD: "ⴰⵎⵓⵔ ⵏ ⵜⵡⵏⵏⴰⴹⵜ",
  CNI: "ⴰⴳⵔⴰⵡ ⴰⵏⴰⵎⵓⵔ ⴰⵎⵓⵏⴰⵏ",
  PSD: "ⴰⵎⵓⵔ ⵏ ⵜⵎⵙⵎⵓⵏⵜ ⵜⴰⵎⴰⴳⵔⴰⵡⵜ",
  PDI: "ⴰⵎⵓⵔ ⵏ ⵜⵎⴰⴳⵔⴰⵡⵜ ⴷ ⵓⵙⵉⵎⴰⵏ",
  PLJS: "ⴰⵎⵓⵔ ⵏ ⵜⵍⴻⵍⵍⵉ ⴷ ⵜⴰⵣⵔⴼⵜ",
  PRD: "ⴰⵎⵓⵔ ⵏ ⵓⵙⵏⴼⵍ ⴷ ⵜⵏⴼⵍⵉⵜ",
  PCS: "ⴰⵎⵓⵔ ⵏ ⵡⴰⵎⵎⴰⵙ",
  PND: "ⴰⵎⵓⵔ ⴰⵏⴰⵎⵓⵔ ⴰⵎⴰⴳⵔⴰⵡ",
  "Al Ahd": "ⴰⵎⵓⵔ ⵏ ⵓⵄⴰⵀⴷ",
  PGVM: "ⴰⵎⵓⵔ ⵏ ⵓⵣⵍⵎⴰⴹ ⴰⵣⴳⵣⴰⵡ",
  PML: "ⴰⵎⵓⵔ ⴰⵎⵖⵔⴰⴱⵉ ⴰⵍⵉⴱⵉⵔⴰⵍ",
  PNUD: "ⴰⵎⵓⵔ ⵏ ⵜⵎⵓⵏⵜ ⴷ ⵜⵎⴰⴳⵔⴰⵡⵜ",
  SAP: "ⵡⴰⵔ ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ",
};

const allPartiesFocus_zgh: Record<string, string> = {
  RNI: "ⴰⵍⵉⴱⵉⵔⴰⵍⵉⵣⵎ ⴰⴷⵎⵙⴰⵏ, ⴰⵙⵎⴰⵢⵏⵓ",
  PAM: "ⵜⴰⵜⵔⴰⵔⵜ, ⵜⴰⵏⴼⵍⵉⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ",
  PI: "ⵜⴰⵏⴰⵎⵓⵔⵜ, ⵉⵙⵏⴼⴰⵍⵏ ⵉⵏⴰⵎⵓⵏⵏ",
  USFP: "ⵜⴰⵎⴰⴳⵔⴰⵡⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ",
  MP: "ⴰⵎⴰⴹⴰⵍ ⴰⴼⴰⵔⴰⵏ, ⵜⴰⴽⵔⵣⴰ",
  PPS: "ⵜⴰⵎⴳⴳⵉⵜ, ⵜⴰⵡⵏⵏⴰⴹⵜ",
  UC: "ⴰⴷⵓⵙⵜⵓⵔⴰⵏⵉⵣⵎ",
  PJD: "ⴰⵙⵉⵍⴰⵎ, ⴰⵙⵏⴼⵍ",
  MDS: "ⵜⴰⵎⴰⴳⵔⴰⵡⵜ ⵜⴰⵏⴰⵎⵓⵏⵜ",
  FFD: "ⵜⵉⵖⵔⵎⵉⵡⵉⵏ ⵜⵉⵎⴰⴳⵔⴰⵡⵉⵏ",
  UMD: "ⵜⴰⵎⵓⵏⵜ ⴷ ⵜⵎⴰⴳⵔⴰⵡⵜ",
  PAI: "ⵜⴰⵎⴳⴳⵉⵜ, ⵓⵙⵉⵎⴰⵏ",
  PRV: "ⵜⵉⵏⴷⵉⵜⵉⵏ, ⵜⵓⴳⴳⴰⵙ, ⵜⴰⵙⵔⵜⵉⵜ ⵜⴰⵅⵍⵇⴰⵏⵜ",
  PT: "ⵉⵣⵔⴼⴰⵏ ⵏ ⵉⵎⵀⴰⵍⵏ",
  PSU: "ⴰⵣⵍⵎⴰⴹ ⴰⵏⴰⵎⵓⵏ",
  PEDD: "ⵜⴰⵡⵏⵏⴰⴹⵜ, ⵜⴰⵏⴼⵍⵉⵜ ⵜⴰⵙⵓⵍⴰⵏⵜ",
  CNI: "ⵜⴰⵎⵓⵏⵜ ⵜⴰⵏⴰⵎⵓⵔⵜ",
  PSD: "ⵜⴰⵎⵙⵎⵓⵏⵜ ⵜⴰⵎⴰⴳⵔⴰⵡⵜ",
  PDI: "ⵜⴰⵎⴰⴳⵔⴰⵡⵜ, ⵓⵙⵉⵎⴰⵏ",
  PLJS: "ⵜⴰⵍⴻⵍⵍⵉ, ⵜⴰⵣⵔⴼⵜ",
  PRD: "ⴰⵙⵏⴼⵍ, ⵜⴰⵏⴼⵍⵉⵜ",
  PCS: "ⴰⵡⴰⵎⵎⴰⵙ",
  PND: "ⴰⵏⴰⵎⵓⵔ, ⴰⵎⴰⴳⵔⴰⵡ",
  "Al Ahd": "ⴰⵄⴰⵀⴷ ⴰⵎⴰⴳⵔⴰⵡ",
  PGVM: "ⵜⴰⵡⵏⵏⴰⴹⵜ, ⴰⵣⵍⵎⴰⴹ ⴰⵣⴳⵣⴰⵡ",
  PML: "ⴰⵍⵉⴱⵉⵔⴰⵍⵉⵣⵎ, ⵜⴰⵜⵔⴰⵔⵜ",
  PNUD: "ⵜⴰⵎⵓⵏⵜ, ⵜⴰⵎⴰⴳⵔⴰⵡⵜ",
  SAP: "ⴰⵙⵉⵎⴰⵏ, ⵜⵉⵖⵔⵎⵉⵡⵉⵏ",
};

export const translations: Record<Lang, Translations> = {
  fr: {
    nav: { home: "Accueil", why: "Pourquoi participer", parties: "Partis politiques", resources: "Ressources", engage: "S'engager" },
    hero: {
      badge: "🇲🇦 Plateforme Citoyenne du Maroc",
      title1: "Kayn'in, L'",
      titleHighlight: "espace citoyen",
      title2: " nouvelle génération.",
      subtitle: "Ici, on scrolle. On tag. On poste. On s’engage !",
      voteBtn: "Je veux voter",
      engageBtn: "Je veux m'engager",
      observeBtn: "Je veux observer",
      candidatBtn: "Je veux être candidat",
      stats: [
        { value: "17M+", label: "Électeurs inscrits" },
        { value: "30%", label: "Quota femmes" },
        { value: "34", label: "Partis politiques" },
        { value: "395", label: "Sièges au parlement" },
      ],
    },
    why: {
      tag: "Pourquoi participer",
      title1: "Votre engagement fait la ",
      titleHighlight: "différence",
      description: "La participation politique n'est pas seulement un devoir, c'est une opportunité de transformer votre quotidien.",
      reasons: [
        { title: "Représenter votre communauté", description: "Les femmes et les jeunes représentent plus de 60% de la population. Votre participation garantit que vos besoins sont entendus." },
        { title: "Défendre vos droits", description: "Le vote est un droit constitutionnel. En participant, vous protégez les acquis et façonnez les politiques qui vous concernent." },
        { title: "Innover pour l'avenir", description: "Apportez des idées nouvelles et des perspectives fraîches aux institutions politiques pour un Maroc plus moderne et inclusif." },
        { title: "Renforcer la démocratie", description: "Chaque voix compte. Une participation massive renforce la légitimité démocratique et la stabilité du pays." },
      ],
      pageTitle: "Pourquoi participer à la vie publique ?",
      pageSubtitle: "Comprendre l'importance de votre engagement citoyen pour un Maroc plus démocratique, inclusif et prospère.",
      detailedReasons: [
        {
          title: "🗳️ Votre vote est votre pouvoir",
          description: "Le vote est le moyen le plus direct d'influencer les décisions qui affectent votre vie quotidienne. Chaque bulletin compte.",
          details: [
            "La Constitution marocaine de 2011 garantit le droit de vote à tous les citoyens majeurs",
            "Les élections communales déterminent directement la gestion de votre quartier et ville",
            "Les élections régionales influencent les projets de développement de votre région",
            "Les élections législatives déterminent les lois qui régissent le pays",
          ],
        },
        {
          title: "👩 Les femmes au cœur du changement",
          description: "La participation des femmes est essentielle pour une démocratie véritablement représentative. Le Maroc a fait des avancées significatives.",
          details: [
            "Le quota de 30% garantit une représentation minimum des femmes au Parlement",
            "Les femmes représentent 51% de la population — leur voix doit être entendue",
            "L'article 19 de la Constitution garantit l'égalité homme-femme",
            "De plus en plus de femmes occupent des postes de responsabilité politique",
          ],
        },
        {
          title: "🧑‍🎓 Les jeunes, moteur de l'avenir",
          description: "Avec plus de 40% de la population ayant moins de 25 ans, les jeunes sont la force motrice du Maroc de demain.",
          details: [
            "L'âge minimum pour voter est de 18 ans — exercez votre droit dès que possible",
            "Les jeunes apportent des perspectives nouvelles et innovantes",
            "La participation des jeunes renforce la légitimité des institutions",
            "Les politiques éducatives et d'emploi sont directement liées au vote",
          ],
        },
        {
          title: "🏛️ Renforcer les institutions démocratiques",
          description: "Une participation massive renforce la légitimité des élus et la qualité de la gouvernance.",
          details: [
            "Un taux de participation élevé rend les élus plus redevables",
            "La diversité des électeurs assure une meilleure représentation",
            "La démocratie participative complète la démocratie représentative",
            "Chaque région mérite d'être équitablement représentée",
          ],
        },
      ],
    },
    parties: {
      tag: "Les partis politiques",
      title1: "Découvrez les ",
      titleHighlight: "programmes",
      description: "Partis représentés au Parlement actuel.",
      seats: "sièges",
      viewProgram: "Programme",
      visitWebsite: "Site web",
      otherPartiesTitle: "Autres partis représentés au Parlement",
      otherPartiesDescription: "Ces partis disposent également de sièges au Parlement.",
      nonParliamentaryTitle: "Autres partis politiques marocains",
      nonParliamentaryDescription: "Partis non représentés au Parlement actuel.",
      partiesFocus: allPartiesFocus_fr,
      partiesNames: allPartiesNames_fr,
    },
    actions: {
      tag: "Actions citoyennes",
      title1: "Engagez-vous dans les ",
      titleHighlight: "actions citoyennes",
      description: "Participez activement à la vie publique à travers les pétitions, doléances, formations, ateliers et chantiers citoyens.",
      usefulLinks: "Liens utiles",
      items: [
        {
          title: "Pétitions citoyennes",
          description: "Initiez ou signez des pétitions pour porter vos revendications auprès des institutions élues.",
          cta: "En savoir plus",
          slug: "petitions",
          content: [
            "La Constitution de 2011 (article 15) garantit le droit de pétition pour tous les citoyens.",
            "Les pétitions peuvent être adressées au Parlement, aux conseils régionaux ou communaux.",
            "Une pétition doit être signée par un nombre minimum de citoyens inscrits sur les listes électorales.",
            "La loi organique n° 44-14 définit les conditions et procédures de dépôt des pétitions.",
            "Les pétitions sont examinées par les commissions compétentes qui doivent rendre une réponse motivée.",
          ],
          links: [
            { label: "Portail des pétitions - Chambre des Représentants", url: "https://www.chambredesrepresentants.ma" },
            { label: "Espace pétitions - e-Participation", url: "https://www.eparticipation.ma" },
            { label: "Loi organique sur le droit de pétition", url: "https://www.sgg.gov.ma" },
          ],
        },
        {
          title: "Doléances et réclamations",
          description: "Soumettez vos doléances aux institutions compétentes pour défendre vos droits.",
          cta: "Déposer une doléance",
          slug: "doleances",
          content: [
            "Le Médiateur du Royaume reçoit les doléances des citoyens contre les décisions administratives.",
            "Les conseils communaux et régionaux disposent de mécanismes de réception des doléances.",
            "Le Conseil National des Droits de l'Homme (CNDH) traite les plaintes liées aux droits humains.",
            "La plateforme Chikaya permet de déposer des réclamations en ligne auprès des administrations publiques.",
          ],
          links: [
            { label: "Chikaya - Portail national des réclamations", url: "https://www.chikaya.ma" },
            { label: "Médiateur du Royaume", url: "https://www.mediateur.ma" },
            { label: "Conseil National des Droits de l'Homme", url: "https://www.cndh.ma" },
          ],
        },
        {
          title: "Formations citoyennes",
          description: "Participez à des formations sur vos droits civiques, la gouvernance locale et la participation politique.",
          cta: "Découvrir les formations",
          slug: "formations",
          content: [
            "Des formations sur la citoyenneté active et les droits civiques sont organisées régulièrement.",
            "Les associations de la société civile proposent des programmes de renforcement des capacités.",
            "Des modules sur la gouvernance locale et les mécanismes de participation sont disponibles.",
            "Les formations couvrent : le processus électoral, le suivi budgétaire, l'advocacy et le plaidoyer.",
          ],
          links: [
            { label: "NDI Maroc - Programmes de formation", url: "https://www.ndi.org/morocco" },
            { label: "Espace Associatif", url: "https://www.espace-associatif.ma" },
            { label: "INDH - Formations", url: "https://www.indh.ma" },
          ],
        },
        {
          title: "Ateliers participatifs",
          description: "Rejoignez des ateliers de concertation pour co-construire les politiques publiques locales.",
          cta: "Participer aux ateliers",
          slug: "ateliers",
          content: [
            "Les ateliers participatifs permettent aux citoyens de contribuer à l'élaboration des plans de développement.",
            "Les instances de concertation publique sont prévues par la loi organique sur les collectivités territoriales.",
            "Les budgets participatifs impliquent les citoyens dans l'allocation des ressources locales.",
            "Les forums communaux permettent le dialogue direct entre élus et citoyens.",
          ],
          links: [
            { label: "DGCL - Collectivités territoriales", url: "https://www.collectivites-territoriales.gov.ma" },
            { label: "Portail de la démocratie participative", url: "https://www.participation.ma" },
          ],
        },
        {
          title: "Chantiers citoyens",
          description: "Participez aux chantiers de développement communautaire et aux initiatives de volontariat.",
          cta: "Rejoindre un chantier",
          slug: "chantiers",
          content: [
            "Les chantiers citoyens mobilisent les jeunes et les communautés pour des projets de développement local.",
            "Le service civique permet aux jeunes de contribuer à des projets d'intérêt général.",
            "Les initiatives de volontariat couvrent l'éducation, la santé, l'environnement et l'infrastructure.",
            "L'INDH (Initiative Nationale pour le Développement Humain) soutient les projets de développement communautaire.",
          ],
          links: [
            { label: "INDH - Projets communautaires", url: "https://www.indh.ma" },
            { label: "Volontariat au Maroc", url: "https://www.entraide.ma" },
            { label: "Agence de Développement Social", url: "https://www.ads.gov.ma" },
          ],
        },
      ],
    },
    resources: {
      tag: "Ressources",
      title1: "Tout ce qu'il faut ",
      titleHighlight: "savoir",
      description: "Des guides pratiques pour vous accompagner dans votre parcours citoyen.",
      items: [
        {
          title: "S'inscrire sur les listes électorales",
          description: "Guide complet pour vous inscrire et vérifier votre inscription avant les élections.",
          cta: "Consulter le guide", slug: "inscription",
          content: [
            "L'inscription sur les listes électorales est obligatoire pour pouvoir voter aux élections au Maroc.",
            "Vous devez être de nationalité marocaine et âgé(e) d'au moins 18 ans.",
            "Munissez-vous de votre Carte Nationale d'Identité (CNIE) en cours de validité.",
            "Rendez-vous à l'autorité locale (caïdat ou annexe administrative) de votre lieu de résidence.",
            "La période d'inscription est ouverte du 15 mai 2026 au 13 juin 2026 (Arrêté 690.26).",
          ],
          links: [
            { label: "Vérifier votre inscription", url: "/voter" },
            { label: "Portail des listes électorales générales", url: "https://www.listeselectorales.ma/fr/index.aspx" },
            { label: "CNIE - Prise de rendez-vous", url: "https://www.cnie.ma" },
          ],
        },
        {
          title: "Devenir candidat(e) : mode d'emploi",
          description: "Conditions d'éligibilité, dossier de candidature et processus complet.",
          cta: "En savoir plus", slug: "candidature",
          content: [
            "Pour se présenter aux élections, il faut être de nationalité marocaine, inscrit sur les listes électorales et ne pas être frappé d'inéligibilité.",
            "L'âge minimum est de 18 ans pour les élections communales et 23 ans pour les législatives.",
            "La candidature se fait via un parti politique ou en tant qu'indépendant (sous conditions).",
            "Le dossier de candidature comprend : demande de candidature, copie CNIE, attestation d'inscription, déclaration de candidature.",
            "Le dépôt de candidature se fait auprès des autorités compétentes dans les délais fixés par la loi.",
          ],
          links: [
            { label: "Loi organique relative à la Chambre des représentants", url: "https://www.chambredesrepresentants.ma" },
            { label: "Conseil National des Droits de l'Homme", url: "https://www.cndh.ma" },
            { label: "Haute Autorité de la Communication Audiovisuelle", url: "https://www.haca.ma" },
          ],
        },
        {
          title: "Trouver votre bureau de vote",
          description: "Localisez votre bureau de vote le plus proche et consultez les horaires.",
          cta: "Localiser", slug: "bureau-vote",
          content: [
            "Chaque électeur est rattaché à un bureau de vote en fonction de son lieu de résidence.",
            "Vous pouvez vérifier votre bureau de vote en ligne ou auprès de l'autorité locale.",
            "Le jour du scrutin, les bureaux de vote sont ouverts de 8h à 19h.",
            "Présentez-vous avec votre Carte Nationale d'Identité en cours de validité.",
          ],
          links: [
            { label: "Rechercher votre bureau de vote", url: "/voter" },
            { label: "Portail des listes électorales générales", url: "https://www.listeselectorales.ma/fr/index.aspx" },
          ],
        },
        {
          title: "Calendrier électoral 2026",
          description: "Dates clés des prochaines échéances électorales au niveau communal, régional et national.",
          cta: "Voir les dates", slug: "calendrier",
          content: [
            "Date du scrutin des législatives : 23 septembre 2026.",
            "Les élections communales, régionales et législatives sont organisées selon un calendrier fixé par le gouvernement.",
            "Les périodes de campagne électorale sont strictement encadrées par la loi.",
            "Les résultats sont proclamés par les commissions compétentes.",
          ],
          links: [
            { label: "Chambre des Représentants", url: "https://www.chambredesrepresentants.ma" },
            { label: "Chambre des Conseillers", url: "https://www.chambredesconseillers.ma" },
            { label: "Cour Constitutionnelle", url: "https://www.cour-constitutionnelle.ma" },
          ],
        },
        {
          title: "Budget citoyen",
          description: "Consultez les budgets citoyens de l'État, des régions, provinces, préfectures et communes.",
          cta: "Consulter les budgets", slug: "budget-citoyen",
          content: [
            "Le budget citoyen est une présentation simplifiée du budget de l'État destinée aux citoyens pour assurer la transparence financière.",
            "Le Ministère de l'Économie et des Finances publie annuellement le budget citoyen de l'État.",
            "Les régions, provinces et préfectures publient également leurs budgets citoyens conformément à la loi organique sur les collectivités territoriales.",
            "Les communes sont tenues de rendre accessible leur budget aux citoyens dans un format compréhensible.",
            "Le budget citoyen présente les grandes orientations budgétaires, les recettes, les dépenses et les indicateurs de performance.",
            "Les citoyens peuvent suivre l'exécution budgétaire et participer aux consultations sur les priorités d'investissement.",
          ],
          links: [
            { label: "Budget citoyen de l'État - MEF", url: "https://www.finances.gov.ma/fr/Pages/budget-citoyen.aspx" },
            { label: "Portail Open Budget - MEF", url: "https://www.budget.finances.gov.ma" },
            { label: "DGCL - Budgets des collectivités territoriales", url: "https://www.collectivites-territoriales.gov.ma" },
            { label: "Portail Open Data du Maroc", url: "https://data.gov.ma" },
            { label: "Cour des Comptes - Rapports", url: "https://www.courdescomptes.ma" },
          ],
        },
        {
          title: "Budget Sensible au Genre (BSG)",
          description: "Publications et budgets intégrant la dimension genre pour une allocation équitable des ressources.",
          cta: "Consulter le BSG", slug: "bsg",
          content: [
            "Le Budget Sensible au Genre (BSG) est une approche budgétaire qui intègre la dimension genre dans la programmation, l'exécution et l'évaluation des budgets publics.",
            "Le Maroc est pionnier en Afrique en matière de budgétisation sensible au genre, initiée dès 2002.",
            "Le rapport sur le Budget axé sur les résultats tenant compte de l'aspect genre accompagne chaque loi de finances.",
            "Le Centre d'Excellence pour la Budgétisation Sensible au Genre (CE-BSG) coordonne les efforts nationaux.",
            "Les ministères sectoriels intègrent progressivement la dimension genre dans leurs budgets et programmes.",
            "Les collectivités territoriales sont encouragées à adopter l'approche BSG dans leurs plans de développement.",
          ],
          links: [
            { label: "Rapport Genre - MEF", url: "https://www.finances.gov.ma/fr/Pages/rapport-genre.aspx" },
            { label: "CE-BSG - Centre d'Excellence", url: "https://www.cebsg.finances.gov.ma" },
            { label: "ONU Femmes Maroc", url: "https://morocco.unwomen.org" },
            { label: "HCP - Statistiques genre", url: "https://www.hcp.ma" },
            { label: "MFFES - Publications", url: "https://www.social.gov.ma" },
          ],
        },
      ],
    },
    cta: {
      title1: "Le changement commence avec ",
      titleHighlight: "vous",
      description: "Rejoignez des milliers de citoyens engagés. Informez-vous et participez activement à la vie publique.",
      btn1: "Je veux voter",
      btn2: "Je veux m'engager",
    },
    footer: {
      description: "Plateforme citoyenne pour encourager la participation des femmes et des jeunes à la vie publique au Maroc.",
      usefulLinks: "Liens utiles",
      links: ["Inscription électorale", "Guide du candidat", "Partis politiques", "Actions citoyennes", "Observateur Électoral"],
      contact: "Contact",
      copyright: "© 2026 Kayn'in. Tous droits réservés.",
    },
    voter: {
      title: "Vérifiez votre inscription électorale",
      subtitle: "Assurez-vous que vous êtes inscrit(e) sur les listes électorales et trouvez votre bureau de vote.",
      checkTitle: "Vérification de l'inscription",
      checkDescription: "Consultez le portail officiel des listes électorales pour vérifier votre inscription et trouver votre bureau de vote.",
      checkBtn: "Vérifier mon inscription",
      findTitle: "Trouver votre bureau de vote",
      findDescription: "Sélectionnez votre localisation pour identifier votre bureau de vote.",
      region: "Région",
      province: "Province / Préfecture",
      commune: "Commune",
      bureau: "Bureau de vote",
      searchBtn: "Rechercher",
    },
    engagement: {
      title: "Je veux m'engager",
      subtitle: "Contactez un parti politique pour exprimer votre intérêt à participer à la vie publique.",
      party: "Parti politique",
      selectParty: "Sélectionnez un parti",
      object: "Objet",
      firstName: "Prénom",
      lastName: "Nom",
      genre: "Genre",
      selectGenre: "Sélectionnez un genre",
      genderOptions: { male: "Homme", female: "Femme" },
      age: "Âge",
      selectAge: "Sélectionnez votre tranche d'âge",
      ageOptions: { "18-24": "18-24 ans", "25-34": "25-34 ans", "35-44": "35-44 ans", "45-54": "45-54 ans", "55-64": "55-64 ans", "65+": "65 ans et plus" },
      profession: "Secteur / Profession",
      selectProfession: "Sélectionnez votre secteur",
      region: "Région",
      province: "Province / Préfecture",
      commune: "Commune",
      phone: "Téléphone",
      email: "Email",
      message: "Message",
      declaration: "J'accepte que les données de ce formulaire soient collectées et traitées par le parti sélectionné dans le but de gérer ma demande d'engagement.",
      cndpDisclaimer: "",
      submit: "Envoyer ma demande",
      successTitle: "Message envoyé !",
      successMessage: "Votre message a été enregistré et sera transmis au parti politique concerné. Vous recevrez une notification de confirmation.",
      errors: { required: "Ce champ est obligatoire", email: "Adresse email invalide", phone: "Numéro de téléphone invalide" },
    },
    common: { backHome: "Retour à l'accueil", loading: "Chargement..." },
    candidacy: {
      title: "Législatives 2026 : Le Parcours du",
      titleHighlight: "Candidat",
      subtitle: "Découvrez les conditions, avantages, parcours, aides financières et incompatibilités pour se présenter aux prochaines élections au Maroc.",
      btnParcours: "Parcours de candidature",
      plafondsCard: {
        label: "Plafond Légal Max",
        amount: "600 000",
        currency: "DHS"
      },
      tabGeneral: "Parcours Général",
      tabYouth: "Jeunes -35 ans",
      eligibilityTitle: "Conditions d'Éligibilité",
      eligibilityItems: [
        { title: "Nationalité", desc: "Être de nationalité marocaine. Pour siéger à la Chambre des Représentants, la nationalité marocaine doit être établie de manière irréfutable." },
        { title: "Âge Requis (Législatives)", desc: "Vous devez avoir 23 ans révolus à la date du scrutin (contrairement aux communales qui requièrent 18 ans)." },
        { title: "Jouissance des droits", desc: "Le candidat doit être dûment inscrit sur les listes électorales, et jouir de tous ses droits civils et politiques (absence de condamnation définitive)." }
      ],
      financeTitle: "Financement & Dépenses",
      subventionsTitle: "Subventions & Nouveautés 2026",
      subventionsDesc: "L'État a significativement renforcé le soutien public. Le montant de l'aide publique est multiplié par six (x6) pour chaque siège remporté par une femme, un jeune de moins de 35 ans ou un Marocain résidant à l'étranger (MRE).",
      subventionsItems: [
        "Avance publique accordée de droit aux grands partis",
        "Le taux de remboursement des dépenses de campagne peut atteindre jusqu'à 75% pour les jeunes"
      ],
      plafondsTitle: "Plafonds des Dépenses Électorales 2026",
      plafondsDesc: "Les nouveaux plafonds des dépenses électorales ont été révisés pour les législatives de 2026 selon le dernier décret.",
      plafondsItems: [
        "600.000 dirhams pour chaque candidat ou candidate",
        "800.000 dirhams pour chaque liste de candidature locale",
        "1.500.000 dirhams pour chaque liste de candidature régionale"
      ],
      timelineTitle: "Chronologie de Candidature",
      timelineItems: [
        { title: "L'Engagement ou l'Indépendance", desc: "SOIT rejoindre un parti politique, SOIT se présenter en Candidat Indépendant (SAP). Les candidats libres (SAP) doivent obligatoirement présenter un document comportant les signatures certifiées d’électeurs de la circonscription (généralement 1% minimum réparti sur les communes)." },
        { title: "L'Accréditation Partisane (Le Cas Échéant)", desc: "Pour les candidats partisans, la direction nationale ou instances régionales accordent l'accréditation « تزكية » exclusive pour représenter le parti sur la circonscription." },
        { title: "Dépôt des Listes (Nouveautés 2026)", desc: "Les listes électorales (scrutin de liste) locales ou régionales (quota femmes) sont déposées de manière allégée grâce à des nouvelles procédures numérisées prévues pour les prochaines élections." },
        { title: "Campagne Très Régulée", desc: "La période de 15 jours sera marquée par une forte digitalisation en 2026 et une rigueur accrue de la HACA et des autorités concernant les contenus numériques et le financement." }
      ],
      incompTitle: "Incompatibilités Légales (Loi Organique)",
      incompDesc: "Selon la loi organique relative à la Chambre des Représentants, certaines qualités ou fonctions interdisent formellement l'éligibilité pour garantir la séparation des pouvoirs et l'impartialité.",
      incompNote: "* Note : Il est également impossible de cumuler la députation au Parlement avec un poste au Gouvernement, la présidence d'un autre conseil constitutionnel, ou la direction de sociétés de l'État.",
      incompItems: [
        "Membres de la Cour Constitutionnelle",
        "Magistrats et Juges",
        "Magistrats de la Cour des Comptes",
        "Militaires et Agents de la Force Publique",
        "Walis, Gouverneurs, Pachas, Caïds...",
        "DG des Établissements et Entreprises Publics"
      ],
      ctaTitle: "Prêt à franchir le pas ?",
      ctaDesc: "Rejoignez un parti aujourd'hui, formez-vous, et prenez part à l'effort national.",
      btnParti: "Contacter un parti politique",
      btnAgenda: "Agenda des événements",
      btnHome: "Retour à l'accueil",
      youth: {
        badge: "Nouveau : Décret n° 2.26.311",
        title: "Soutien financier aux candidats de moins de 35 ans",
        desc: "En application des dispositions de l'article 23 de la loi organique n° 27.11 relative à la Chambre des Représentants, le nouveau décret fixe les conditions et les modalités de versement du soutien financier public en faveur des listes de candidature présentées par des candidates et candidats ne dépassant pas 35 ans. Cette mesure vise à encourager la représentativité des jeunes au sein de l'institution législative.",
        eligibilityTitle: "Conditions d'Éligibilité (Jeunes)",
        eligibilityItems: [
          "Les candidats doivent avoir moins de 35 ans (pour les listes locales ou régionales).",
          "Obtenir au moins 2 % des suffrages exprimés dans la circonscription pour décrocher l'aide.",
          "Le soutien est ouvert aux candidats sous l'égide de partis politiques ainsi qu'aux candidats sans appartenance politique (SAP).",
          "Nécessité de remplir toutes les conditions légales encadrant le processus électoral."
        ],
        financeTitle: "Dispositif Exceptionnel de Soutien Financier",
        percentAmount: "75%",
        bonusAmount: "10K",
        womensFundTitle: "Fonds d’appui pour la promotion de la représentativité des femmes",
        womensFundDesc: "Un mécanisme visant à renforcer les capacités des femmes afin de faciliter leur intégration dans la vie politique et électorale nationale.",
        subventionsTitle: "Prise en charge publique (jusqu'à 75%)",
        subventionsDesc: "Afin de faciliter l'accès des jeunes aux élections, le plafond de ce soutien rembourse jusqu'à 75% de vos frais, tiré du budget global de 100 Millions de DHS réservé pour 2026.",
        bonusTitle: "Prime aux partis",
        bonusDesc: "Pour vous encourager, chaque parti reçoit un soutien net de 10 000 DHS pour chaque jeune candidat de moins de 35 ans investi.",
        rulesTitle: "Transparence et Modalités Pratiques",
        rulesItems: [
          "Le plafond est fixé à 75 % du plafond des dépenses électorales autorisées.",
          "La subvention ne peut dépasser 75 % des dépenses effectives engagées et justifiées.",
          "Les candidats s'engagent à ouvrir des comptes bancaires dédiés pour chaque liste afin de tracer rigoureusement les ressources et les dépenses.",
          "Obligation d'établir un compte de campagne visé par un expert-comptable et de le déposer auprès de la Cour des Comptes."
        ]
      }
    },
    observateur: {
      heroTitle: "Devenir Observateur Électoral",
      heroBadge: "Loi n° 30-11",
      heroDesc: "Vos yeux pour garantir la transparence ! Découvrez comment la Loi n° 30-11 permet aux citoyens de sécuriser et de vérifier le bon déroulement des élections au Maroc.",
      definitionTitle: "Définition de l’observation indépendante et neutre des élections",
      definitionDesc: "Selon la loi n° 30-11 fixant les conditions et les modalités de l’observation indépendante et neutre des élections, « on entend par l’observation indépendante et neutre des élections, toute opération ayant pour objet le suivi sur le terrain du déroulement des opérations électorales, la collecte objective, impartiale et neutre des données y afférentes et l’évaluation des conditions de leur organisation et de leur déroulement ainsi que du respect des règles constitutionnelles et des textes législatifs et réglementaires régissant les élections et des normes internationales, et ce à travers des rapports élaborés par les instances concernées... »",
      section1Title: "Institutions habilitées à observer",
      institutions: [
        { title: "Institutions Nationales", desc: "Les institutions nationales habilitées en vertu de la loi à accomplir les missions d'observation électorale (comme le Conseil National des Droits de l'Homme)." },
        { title: "ONG Marocaines", desc: "Les associations de la société civile actives, reconnues pour leur sérieux dans la promotion des valeurs citoyennes et démocratiques." },
        { title: "ONG Internationales", desc: "Les organisations non gouvernementales étrangères dotées d'une indépendance et d'une objectivité reconnues, s'intéressant à l'observation." }
      ],
      section2Title: "La Commission Spéciale d'Accréditation",
      section2Desc: "Créée auprès du Conseil National des Droits de l'Homme (CNDH), cette commission est l'unique instance chargée de recevoir, d'étudier et de valider les demandes d'accréditation.",
      conditionsTitle: "Conditions d'éligibilité",
      conditions: [
        "Être de nationalité marocaine (ou mandaté par l'ONG étrangère).",
        "Être majeur et jouir de ses droits civiques et politiques.",
        "N'avoir subi aucune condamnation non réhabilitée.",
        "Ne pas être candidat ou membre d'un parti politique en lice."
      ],
      modalitesTitle: "Modalités d'accréditation",
      modalites: [
        "Dépôt d'une demande écrite dans les délais fixés par la Commission.",
        "Évaluation selon des critères stricts d'indépendance et d'expertise.",
        "Délivrance d'une carte d'observateur personnelle et incessible.",
        "Signature et respect de la \"Charte de l'Observation Électorale\"."
      ],
      section3Title: "Droits et Obligations de l'Observateur",
      droitsTitle: "Vos Droits au créneau",
      droits: [
        "Libre accès aux bureaux de vote (sur présentation obligatoire du badge).",
        "Liberté de circulation dans les limites de la circonscription autorisée.",
        "Droit d'obtenir les informations relatives au vote et d'assister aux résultats.",
        "Protection juridique totale dans l'exercice normatif de la mission."
      ],
      obligationsTitle: "Vos Obligations strictes",
      obligations: [
        "Aucun parti pris. Faire preuve d'une objectivité et d'une impartialité totales.",
        "Ne jamais s'immiscer ni intervenir dans le travail du chef de bureau de vote.",
        "S'abstenir de toute déclaration sur les résultats avant la clôture totale.",
        "Rédaction d'un rapport objectif et détaillé destiné uniquement à la Commission."
      ],
      cndhTitle: "Commission Spéciale d'Accréditation des Observateurs des Élections",
      cndhBtn: "Visiter la plateforme officielle",
      downloadTitle: "Télécharger vos formulaires",
      downloadDesc: "Par décision de la Commission, sont mis à disposition du public les formulaires de demande d'accréditation ainsi que la charte de l'Observation Indépendante et Neutre des Élections sur le portail en ligne.",
      suggestedTitle: "Cela pourrait aussi vous intéresser :",
      sugVoterTitle: "Vérifier mon inscription",
      sugVoterDesc: "Assurez-vous que vous êtes bien sur les listes électorales pour voter le jour J.",
      sugQuizTitle: "Quiz d'affinité politique",
      sugQuizDesc: "Trouvez avec quel parti politique vous avez le plus d'affinité.",
      sugPartiTitle: "S'engager en politique",
      sugPartiDesc: "Découvrez comment devenir membre actif d'un parti ou vous présenter."
    },
    expectations: {
      badge: "Voix des citoyens",
      title: "Voix des Citoyens : Attentes et priorités des citoyens",
      subtitle: "Participer à l'expression des besoins et des attentes",
      respondents: "répondants",
      btnSpeak: "S'exprimer",
      updatedAt: "Actualisé à",
      filters: { all: "Tous", citizen: "Adultes +30", young: "Jeunes -29", women: "Femmes", ong: "ONG" },
      kpiRespondents: "Répondants",
      kpiUnique: "Priorités uniques",
      kpiTop: "Top mention",
      kpiTop3: "Taux top 3",
      emptyData: "Aucune donnée pour ce filtre",
      cloudTitle: "Nuage de priorités —",
      cloudNote: "La taille des étiquettes reflète la fréquence des mentions",
      barTitle: "Top 10 priorités",
      mentions: "mentions",
      frequency: "Fréquence",
      tableTitle: "Classement complet des priorités",
      colRank: "#",
      colPriority: "Priorité",
      colMentions: "Mentions",
      colPct: "%",
      colPopularity: "Popularité",
      tableNote: "Priorités soumises volontairement lors de l'inscription",
      btnLess: "Voir moins",
      modalTitle: "S'exprimer",
      modalSubtitle: "Vos 2 propositions à prendre en considération",
      priority1: "Priorité n°1",
      priority2: "Priorité n°2",
      maxWords: "(max 3 mots)",
      placeholder1: "Ex: Emploi jeunes",
      placeholder2: "Ex: Santé accessible",
      reminder: "⚡ Rappel : Vos priorités seront validées par l'administration avant d'apparaître dans le baromètre public.",
      ethicsText: "Je certifie que cette publication respecte la charte éthique de la plateforme (pas de diffamation, discours de haine ou incitation à la violence).",
      btnSubmit: "Soumettre mes priorités",
      successTitle: "Merci pour votre participation !",
      successDesc: "Vos priorités ont été soumises et seront examinées par l'administration avant publication dans le baromètre.",
      btnClose: "Fermer"
    }
  },
  ar: {
    nav: { home: "الرئيسية", why: "لماذا المشاركة", parties: "الأحزاب السياسية", resources: "الموارد", engage: "شارك الآن" },
    hero: {
      badge: "🇲🇦 منصة المواطنة المغربية",
      title1: "Kayn'in، ",
      titleHighlight: "الفضاء المواطن",
      title2: " من الجيل الجديد.",
      subtitle: "هنا، نتصفح. نضع إشارات. نتقاسم. وننخرط!",
      voteBtn: "أريد التصويت",
      engageBtn: "أريد المشاركة",
      observeBtn: "أريد أن ألاحظ",
      candidatBtn: "أريد الترشيح",
      stats: [
        { value: "+17 مليون", label: "ناخب مسجل" },
        { value: "30%", label: "حصة النساء" },
        { value: "34", label: "حزب سياسي" },
        { value: "395", label: "مقعد برلماني" },
      ],
    },
    why: {
      tag: "لماذا المشاركة",
      title1: "مشاركتك تصنع ",
      titleHighlight: "الفرق",
      description: "المشاركة السياسية ليست مجرد واجب، بل هي فرصة لتغيير حياتك اليومية.",
      reasons: [
        { title: "تمثيل مجتمعك", description: "النساء والشباب يمثلون أكثر من 60% من السكان. مشاركتك تضمن أن احتياجاتك مسموعة." },
        { title: "الدفاع عن حقوقك", description: "التصويت حق دستوري. بالمشاركة، تحمي المكتسبات وتشكل السياسات التي تهمك." },
        { title: "الابتكار من أجل المستقبل", description: "قدم أفكاراً جديدة ورؤى حديثة للمؤسسات السياسية لمغرب أكثر حداثة وشمولية." },
        { title: "تعزيز الديمقراطية", description: "كل صوت مهم. المشاركة الواسعة تعزز الشرعية الديمقراطية واستقرار البلاد." },
      ],
      pageTitle: "لماذا المشاركة في الحياة العامة؟",
      pageSubtitle: "فهم أهمية مشاركتك المواطنة من أجل مغرب أكثر ديمقراطية وشمولية وازدهاراً.",
      detailedReasons: [
        { title: "🗳️ صوتك هو قوتك", description: "التصويت هو الوسيلة الأكثر مباشرة للتأثير على القرارات التي تؤثر على حياتك اليومية.",
          details: ["يكفل دستور 2011 حق التصويت لجميع المواطنين البالغين", "الانتخابات الجماعية تحدد مباشرة تدبير حيك ومدينتك", "الانتخابات الجهوية تؤثر على مشاريع التنمية في جهتك", "الانتخابات التشريعية تحدد القوانين التي تحكم البلاد"] },
        { title: "👩 المرأة في قلب التغيير", description: "مشاركة المرأة ضرورية لديمقراطية تمثيلية حقيقية.",
          details: ["حصة 30% تضمن الحد الأدنى من تمثيل المرأة في البرلمان", "النساء يمثلن 51% من السكان — يجب سماع صوتهن", "الفصل 19 من الدستور يكفل المساواة بين الرجل والمرأة", "تتزايد أعداد النساء في المناصب السياسية المسؤولة"] },
        { title: "🧑‍🎓 الشباب، محرك المستقبل", description: "مع أكثر من 40% من السكان دون 25 سنة، الشباب هم القوة المحركة لمغرب الغد.",
          details: ["السن الأدنى للتصويت هو 18 سنة — مارس حقك في أقرب وقت", "الشباب يجلبون رؤى جديدة ومبتكرة", "مشاركة الشباب تعزز شرعية المؤسسات", "سياسات التعليم والتشغيل مرتبطة مباشرة بالتصويت"] },
        { title: "🏛️ تعزيز المؤسسات الديمقراطية", description: "المشاركة الواسعة تعزز شرعية المنتخبين وجودة الحكامة.",
          details: ["نسبة مشاركة عالية تجعل المنتخبين أكثر مسؤولية", "تنوع الناخبين يضمن تمثيلاً أفضل", "الديمقراطية التشاركية تكمل الديمقراطية التمثيلية", "كل جهة تستحق أن تمثل بإنصاف"] },
      ],
    },
    parties: {
      tag: "الأحزاب السياسية",
      title1: "اكتشف ",
      titleHighlight: "البرامج",
      description: "اطلع على الأحزاب السياسية المغربية الممثلة في البرلمان ورؤاها وبرامجها.",
      seats: "مقعد",
      viewProgram: "البرنامج",
      visitWebsite: "الموقع",
      otherPartiesTitle: "أحزاب أخرى ممثلة في البرلمان",
      otherPartiesDescription: "هذه الأحزاب تتوفر أيضاً على مقاعد في البرلمان.",
      nonParliamentaryTitle: "أحزاب سياسية مغربية أخرى",
      nonParliamentaryDescription: "أحزاب غير ممثلة في البرلمان الحالي.",
      partiesFocus: allPartiesFocus_ar,
      partiesNames: allPartiesNames_ar,
    },
    actions: {
      tag: "الأعمال المواطنة",
      title1: "شاركوا في ",
      titleHighlight: "الأعمال المواطنة",
      description: "شاركوا بفعالية في الحياة العامة عبر العرائض والتظلمات والتكوينات والورشات والأوراش المواطنة.",
      usefulLinks: "روابط مفيدة",
      items: [
        { title: "العرائض المواطنة", description: "بادروا أو وقعوا عرائض لإيصال مطالبكم إلى المؤسسات المنتخبة.", cta: "معرفة المزيد", slug: "petitions",
          content: ["يكفل دستور 2011 (الفصل 15) حق تقديم العرائض لجميع المواطنين.", "يمكن توجيه العرائض إلى البرلمان أو المجالس الجهوية أو الجماعية.", "يجب أن تكون العريضة موقعة من عدد أدنى من المواطنين المسجلين.", "القانون التنظيمي 44-14 يحدد شروط وإجراءات إيداع العرائض."],
          links: [{ label: "بوابة العرائض - مجلس النواب", url: "https://www.chambredesrepresentants.ma" }, { label: "المشاركة الإلكترونية", url: "https://www.eparticipation.ma" }] },
        { title: "التظلمات والشكايات", description: "قدموا تظلماتكم للمؤسسات المختصة للدفاع عن حقوقكم.", cta: "تقديم شكاية", slug: "doleances",
          content: ["يتلقى وسيط المملكة تظلمات المواطنين ضد القرارات الإدارية.", "منصة شكاية تمكن من تقديم الشكايات عبر الإنترنت.", "المجلس الوطني لحقوق الإنسان يعالج الشكايات المتعلقة بحقوق الإنسان."],
          links: [{ label: "شكاية - البوابة الوطنية للشكايات", url: "https://www.chikaya.ma" }, { label: "وسيط المملكة", url: "https://www.mediateur.ma" }] },
        { title: "تكوينات مواطنة", description: "شاركوا في تكوينات حول حقوقكم المدنية والحكامة المحلية.", cta: "اكتشف التكوينات", slug: "formations",
          content: ["تنظم تكوينات منتظمة حول المواطنة الفاعلة والحقوق المدنية.", "جمعيات المجتمع المدني تقترح برامج لتعزيز القدرات.", "تتوفر وحدات حول الحكامة المحلية وآليات المشاركة."],
          links: [{ label: "NDI المغرب - برامج التكوين", url: "https://www.ndi.org/morocco" }, { label: "المبادرة الوطنية للتنمية البشرية", url: "https://www.indh.ma" }] },
        { title: "ورشات تشاركية", description: "انضموا إلى ورشات التشاور للمساهمة في بناء السياسات العمومية المحلية.", cta: "المشاركة في الورشات", slug: "ateliers",
          content: ["الورشات التشاركية تمكن المواطنين من المساهمة في إعداد مخططات التنمية.", "الميزانيات التشاركية تشرك المواطنين في تخصيص الموارد المحلية.", "المنتديات الجماعية تتيح الحوار المباشر بين المنتخبين والمواطنين."],
          links: [{ label: "المديرية العامة للجماعات الترابية", url: "https://www.collectivites-territoriales.gov.ma" }] },
        { title: "أوراش مواطنة", description: "شاركوا في أوراش التنمية المجتمعية ومبادرات التطوع.", cta: "الانضمام لورش", slug: "chantiers",
          content: ["الأوراش المواطنة تعبئ الشباب والمجتمعات لمشاريع التنمية المحلية.", "الخدمة المدنية تمكن الشباب من المساهمة في مشاريع ذات منفعة عامة.", "المبادرة الوطنية للتنمية البشرية تدعم مشاريع التنمية المجتمعية."],
          links: [{ label: "المبادرة الوطنية للتنمية البشرية", url: "https://www.indh.ma" }, { label: "وكالة التنمية الاجتماعية", url: "https://www.ads.gov.ma" }] },
      ],
    },
    resources: {
      tag: "الموارد",
      title1: "كل ما تحتاج ",
      titleHighlight: "معرفته",
      description: "أدلة عملية لمرافقتك في مسارك المدني.",
      items: [
        { title: "التسجيل في اللوائح الانتخابية", description: "دليل شامل للتسجيل والتحقق من تسجيلك قبل الانتخابات.", cta: "قراءة الدليل", slug: "inscription",
          content: ["التسجيل في اللوائح الانتخابية إلزامي للتمكن من التصويت.", "يجب أن تكون مغربي الجنسية وبالغاً 18 سنة على الأقل.", "أحضر بطاقتك الوطنية سارية المفعول.", "توجه إلى السلطة المحلية في مقر إقامتك.", "فترة التسجيل مفتوحة من 15 ماي إلى 13 يونيو 2026 (القرار 690.26)."],
          links: [{ label: "التحقق من تسجيلك", url: "/voter" }, { label: "بوابة اللوائح الانتخابية العامة", url: "https://www.listeselectorales.ma/index.aspx" }] },
        { title: "الترشح: دليل عملي", description: "شروط الأهلية وملف الترشح والعملية الكاملة.", cta: "معرفة المزيد", slug: "candidature",
          content: ["يجب أن تكون مغربي الجنسية ومسجلاً في اللوائح الانتخابية.", "السن الأدنى 18 سنة للجماعية و23 سنة للتشريعية.", "الترشح عبر حزب سياسي أو بشكل مستقل."],
          links: [{ label: "مجلس النواب", url: "https://www.chambredesrepresentants.ma" }, { label: "المجلس الوطني لحقوق الإنسان", url: "https://www.cndh.ma" }] },
        { title: "العثور على مكتب التصويت", description: "حدد موقع مكتب التصويت الأقرب إليك.", cta: "تحديد الموقع", slug: "bureau-vote",
          content: ["كل ناخب مرتبط بمكتب تصويت حسب مقر إقامته.", "يمكنك التحقق عبر الإنترنت أو لدى السلطة المحلية.", "يوم الاقتراع، المكاتب مفتوحة من 8 صباحاً إلى 7 مساءً."],
          links: [{ label: "البحث عن مكتب التصويت", url: "/voter" }] },
        { title: "الرزنامة الانتخابية 2026", description: "التواريخ الرئيسية للاستحقاقات الانتخابية المقبلة.", cta: "عرض التواريخ", slug: "calendrier",
          content: ["تاريخ الاقتراع للانتخابات التشريعية: 23 سبتمبر 2026.", "الانتخابات الجماعية والجهوية والتشريعية تنظم وفق رزنامة تحددها الحكومة.", "فترات الحملة الانتخابية مؤطرة بشكل صارم بالقانون."],
          links: [{ label: "مجلس النواب", url: "https://www.chambredesrepresentants.ma" }, { label: "المحكمة الدستورية", url: "https://www.cour-constitutionnelle.ma" }] },
        { title: "الميزانية المواطنة", description: "اطلعوا على الميزانيات المواطنة للدولة والجهات والعمالات والأقاليم والجماعات.", cta: "استشارة الميزانيات", slug: "budget-citoyen",
          content: ["الميزانية المواطنة هي عرض مبسط لميزانية الدولة موجه للمواطنين لضمان الشفافية المالية.", "تنشر وزارة الاقتصاد والمالية سنوياً الميزانية المواطنة للدولة.", "الجهات والعمالات والأقاليم تنشر أيضاً ميزانياتها المواطنة.", "الجماعات ملزمة بإتاحة ميزانياتها للمواطنين بشكل مفهوم.", "يمكن للمواطنين متابعة تنفيذ الميزانية والمشاركة في المشاورات."],
          links: [{ label: "الميزانية المواطنة - وزارة المالية", url: "https://www.finances.gov.ma/fr/Pages/budget-citoyen.aspx" }, { label: "البوابة المفتوحة للميزانية", url: "https://www.budget.finances.gov.ma" }, { label: "المديرية العامة للجماعات الترابية", url: "https://www.collectivites-territoriales.gov.ma" }, { label: "المجلس الأعلى للحسابات", url: "https://www.courdescomptes.ma" }] },
        { title: "الميزانية المستجيبة للنوع الاجتماعي", description: "المنشورات والميزانيات المدمجة لبُعد النوع الاجتماعي.", cta: "استشارة BSG", slug: "bsg",
          content: ["الميزانية المستجيبة للنوع الاجتماعي هي مقاربة تدمج بُعد النوع في البرمجة والتنفيذ والتقييم.", "المغرب رائد في إفريقيا في مجال الميزنة المستجيبة للنوع منذ 2002.", "التقرير حول الميزانية القائمة على النتائج مع مراعاة بُعد النوع يرافق كل قانون مالية.", "مركز التميز للميزنة المستجيبة للنوع ينسق الجهود الوطنية.", "الجماعات الترابية مدعوة لتبني مقاربة النوع في مخططاتها التنموية."],
          links: [{ label: "تقرير النوع - وزارة المالية", url: "https://www.finances.gov.ma/fr/Pages/rapport-genre.aspx" }, { label: "مركز التميز للميزنة المستجيبة للنوع", url: "https://www.cebsg.finances.gov.ma" }, { label: "هيئة الأمم المتحدة للمرأة - المغرب", url: "https://morocco.unwomen.org" }, { label: "المندوبية السامية للتخطيط", url: "https://www.hcp.ma" }] },
      ],
    },
    cta: {
      title1: "التغيير يبدأ ",
      titleHighlight: "بك",
      description: "انضم إلى آلاف المواطنين الملتزمين. اطلع وشارك بفعالية في الحياة العامة.",
      btn1: "أريد التصويت",
      btn2: "أريد أن ألاحظ",
    },
    footer: {
      description: "منصة مواطنة لتشجيع مشاركة النساء والشباب في الحياة العامة بالمغرب.",
      usefulLinks: "روابط مفيدة",
      links: ["التسجيل الانتخابي", "دليل المرشح", "الأحزاب السياسية", "الأعمال المواطنة", "مراقب انتخابي"],
      contact: "اتصل بنا",
      copyright: "© 2026 مشاركة. جميع الحقوق محفوظة.",
    },
    voter: {
      title: "تحقق من تسجيلك الانتخابي",
      subtitle: "تأكد من أنك مسجل في اللوائح الانتخابية وابحث عن مكتب التصويت الخاص بك.",
      checkTitle: "التحقق من التسجيل",
      checkDescription: "استشر البوابة الرسمية للوائح الانتخابية للتحقق من تسجيلك والعثور على مكتب التصويت.",
      checkBtn: "التحقق من تسجيلي",
      findTitle: "العثور على مكتب التصويت",
      findDescription: "حدد موقعك للعثور على مكتب التصويت الخاص بك.",
      region: "الجهة",
      province: "الإقليم / العمالة",
      commune: "الجماعة",
      bureau: "مكتب التصويت",
      searchBtn: "بحث",
    },
    engagement: {
      title: "أريد المشاركة",
      subtitle: "تواصل مع حزب سياسي للتعبير عن رغبتك في المساهمة في الحياة العامة.",
      party: "الحزب السياسي",
      selectParty: "اختر حزباً",
      object: "الموضوع",
      firstName: "الاسم الشخصي",
      lastName: "الاسم العائلي",
      genre: "الجنس",
      selectGenre: "اختر الجنس",
      genderOptions: { male: "ذكر", female: "أنثى" },
      age: "العمر",
      selectAge: "اختر فئتك العمرية",
      ageOptions: { "18-24": "18-24 سنة", "25-34": "25-34 سنة", "35-44": "35-44 سنة", "45-54": "45-54 سنة", "55-64": "55-64 سنة", "65+": "65 سنة فما فوق" },
      profession: "القطاع / المهنة",
      selectProfession: "اختر قطاعك",
      region: "الجهة",
      province: "الإقليم / العمالة",
      commune: "الجماعة",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      message: "الرسالة",
      declaration: "أوافق على أن يتم جمع بيانات هذا النموذج ومعالجتها من قبل الحزب المختار بغرض إدارة طلب الانخراط الخاص بي.",
      cndpDisclaimer: "",
      submit: "إرسال طلبي",
      successTitle: "تم الإرسال!",
      successMessage: "تم تسجيل رسالتك. بعد المصادقة من طرف فريقنا، سيتم إرسالها إلى الحزب السياسي المعني. ستتلقى إشعاراً بالتأكيد.",
      errors: { required: "هذا الحقل إلزامي", email: "عنوان بريد إلكتروني غير صالح", phone: "رقم هاتف غير صالح" },
    },
    common: { backHome: "العودة للرئيسية", loading: "جاري التحميل..." },
    candidacy: {
      title: "تشريعيات 2026 : مسار",
      titleHighlight: "المرشح",
      subtitle: "اكتشف الشروط، الامتيازات، المسار، الدعم المالي وحالات التنافي للترشح في الانتخابات القادمة بالمغرب.",
      btnParcours: "مسار الترشيح",
      plafondsCard: {
        label: "السقف القانوني الأقصى",
        amount: "600.000",
        currency: "درهم"
      },
      tabGeneral: "المسار العام",
      tabYouth: "الشباب أقل من 35 سنة",
      eligibilityTitle: "شروط الأهلية",
      eligibilityItems: [
        { title: "الجنسية", desc: "أن يكون من جنسية مغربية. لولوج مجلس النواب، يجب أن تكون الجنسية المغربية مثبتة بصفة قاطعة." },
        { title: "السن المطلوب (التشريعيات)", desc: "يجب أن تبلغ 23 سنة شمسية كاملة في تاريخ الاقتراع (عكس الانتخابات الجماعية التي تتطلب 18 سنة)." },
        { title: "التمتع بالحقوق", desc: "يجب أن يكون المرشح مقيداً في اللوائح الانتخابية، ومتمتعاً بجميع حقوقه المدنية والسياسية (عدم وجود إدانة نهائية)." }
      ],
      financeTitle: "التمويل والمصاريف",
      subventionsTitle: "الدعم والمستجدات 2026",
      subventionsDesc: "تم تعزيز الدعم العمومي بشكل كبير. سيتم مضاعفة الدعم ست مرات (x6) عن كل مقعد تفوز به امرأة، أو شاب (أقل من 35 سنة)، أو مغربي مقيم بالخارج.",
      subventionsItems: [
        "دعم مالي مشجع لتحقيق تكافؤ الفرص",
        "نسبة استرجاع مصاريف الحملة تصل إلى 75% بالنسبة للشباب"
      ],
      plafondsTitle: "سقوف المصاريف الانتخابية 2026",
      plafondsDesc: "تمت مراجعة وتحديد سقوف المصاريف الانتخابية الجديدة بموجب المرسوم الأخير خلال تشريعيات 2026.",
      plafondsItems: [
        "600.000 درهم لكل مترشح أو مترشحة",
        "800.000 درهم بالنسبة لكل لائحة ترشيح محلية",
        "1.500.000 درهم لكل لائحة ترشيح جهوية"
      ],
      timelineTitle: "مراحل الترشيح",
      timelineItems: [
        { title: "الالتزام الحزبي أو الاستقلالية", desc: "إما الانضمام إلى حزب سياسي، أو الترشح كمستقل (بدون انتماء سياسي). يجب على المرشحين المستقلين تقديم لائحة مدعومة بتوقيعات مصادق عليها للناخبين في الدائرة المعنية." },
        { title: "التزكية الحزبية", desc: "بالنسبة للمرشحين الحزبيين، تمنح الأجهزة الوطنية أو الجهوية التزكية الحصرية لتمثيل الحزب في الدائرة الانتخابية." },
        { title: "إيداع اللوائح (مستجدات 2026)", desc: "تودع اللوائح الانتخابية (الاقتراع اللائحي) المحلية أو الجهوية بشكل مبسط بفضل إجراءات رقمنة جديدة مرتقبة." },
        { title: "حملة خاضعة للرقابة", desc: "مدة 15 يوماً ستتميز برقمنة قوية في 2026 ومراقبة مشددة من طرف الهاكا (HACA) والسلطات المختصة." }
      ],
      incompTitle: "حالات التنافي (حسب القانون التنظيمي)",
      incompDesc: "بناءً على القانون التنظيمي المتعلق بمجلس النواب، هناك مهام وصفات تمنع الترشح لضمان فصل السلط والحياد.",
      incompNote: "* ملاحظة: يُمنع كذلك الجمع بين الانتداب البرلماني وعضوية الحكومة، أو رئاسة مؤسسة دستورية أخرى، أو إدارة شركات تابعة للدولة.",
      incompItems: [
        "أعضاء المحكمة الدستورية",
        "القضاة بمختلف المحاكم",
        "قضاة المجلس الأعلى للحسابات",
        "العسكريون وأعوان القوة العمومية",
        "الولاة، العمال، الباشوات، والقواد",
        "المدراء العامون للمؤسسات والمقاولات العمومية"
      ],
      ctaTitle: "مستعد لأخذ المبادرة؟",
      ctaDesc: "انضم إلى حزب اليوم، تكوّن، وساهم في المجهود الوطني.",
      btnParti: "تواصل مع حزب سياسي",
      btnAgenda: "أجندة الفعاليات",
      btnHome: "العودة إلى الرئيسية",
      youth: {
        badge: "جديد: المرسوم رقم 2.26.311",
        title: "دعم مالي للمرشحين أقل من 35 سنة",
        desc: "تطبيقا لأحكام المادة 23 من القانون التنظيمي رقم 27.11 المتعلق بمجلس النواب، يحدد المرسوم الجديد شروط وكيفيات صرف الدعم المالي العمومي لفائدة لوائح الترشيح المقدمة من طرف مترشحات ومترشحين لا تزيد أعمارهم عن 35 سنة. يهدف هذا الإجراء إلى تعزيز تمثيلية الشباب داخل المؤسسة التشريعية.",
        eligibilityTitle: "شروط الأهلية الخاصة بالشباب",
        eligibilityItems: [
          "أن يكون المرشحون أقل من 35 سنة، بالنسبة للوائح المحلية أو الجهوية.",
          "الحصول على نسبة 2% على الأقل من الأصوات المعبر عنها لضمان التمويل.",
          "الاستفادة مفتوحة للمرشحين المنتمين للأحزاب السياسية وكذلك المستقلين.",
          "ضرورة استيفاء جميع الشروط القانونية المؤطرة للعملية الانتخابية."
        ],
        financeTitle: "نظام الدعم المالي الاستثنائي",
        percentAmount: "75%",
        bonusAmount: "10K",
        womensFundTitle: "صندوق الدعم لتشجيع تمثيلية النساء",
        womensFundDesc: "آلية تهدف إلى تعزيز قدرات النساء وتيسير إدماجهن في الحياة السياسية والانتخابية الوطنية.",
        subventionsTitle: "دعم عمومي (في حدود 75%)",
        subventionsDesc: "لتسهيل مشاركة الشباب في الانتخابات، تعوض الدولة إلى غاية 75% من المصاريف، من غلاف مالي مخصص بقيمة 100 مليون درهم لعام 2026.",
        bonusTitle: "تحفيز الأحزاب",
        bonusDesc: "تشجيعا لكم، تمنح الدولة 10.000 درهم كدعم مباشر للحزب عن كل مرشح شاب تتم تزكيته.",
        rulesTitle: "الشفافية والكيفيات العملية",
        rulesItems: [
          "يحدد السقف في حدود 75 في المائة من سقف المصاريف الانتخابية المسموح بها.",
          "يحدد السقف في حدود 75 في المائة من المصاريف الفعلية المنجزة والمصرح بمطابقتها.",
          "يلتزم المترشحون بفتح حسابات بنكية خاصة بكل لائحة لتتبع الموارد والمصاريف بدقة.",
          "إلزامية إعداد حساب الحملة الانتخابية والتأشير عليه من طرف محاسب مختص وإيداعه لدى المجلس الأعلى للحسابات."
        ]
      }
    },
    observateur: {
      heroTitle: "أن تصبح ملاحظا للانتخابات",
      heroBadge: "القانون رقم 30-11",
      heroDesc: "انخراطكم في ضمان الشفافية! اكتشفوا كيف يتيح القانون رقم 30-11 للمواطنين ضمان وتأمين السير العادي للانتخابات بالمغرب.",
      definitionTitle: "ما المقصود بالملاحظة المستقلة والمحايدة للانتخابات ؟",
      definitionDesc: "يقصد بالملاحظة المستقلة والمحايدة للانتخابات كل عملية تهدف، إلى التتبع الميداني لسير العمليات الانتخابية وتجميع معطياتها بموضوعية وتجرد وحياد، وتقييم ظروف تنظيمها وإجرائها ومدى احترامها للقواعد الدستورية والنصوص التشريعية والتنظيمية المتعلقة بالانتخابات والمعايير الدولية...",
      section1Title: "المؤسسات المؤهلة للملاحظة",
      institutions: [
        { title: "المؤسسات الوطنية", desc: "المؤسسات الوطنية المؤهلة بحكم القانون للقيام بمهام الملاحظة الانتخابية (مثل المجلس الوطني لحقوق الإنسان)." },
        { title: "المنظمات غير الحكومية المغربية", desc: "جمعيات المجتمع المدني النشيطة والمشهود لها بالجدية في تعزيز قيم المواطنة والديمقراطية." },
        { title: "المنظمات غير الحكومية الدولية", desc: "المنظمات الأجنبية التي تتمتع باستقلالية وموضوعية وتُعنى بمجال الملاحظة الانتخابية." }
      ],
      section2Title: "اللجنة الخاصة للاعتماد",
      section2Desc: "أُحدثت لدى المجلس الوطني لحقوق الإنسان (CNDH)، وهي الهيئة الوحيدة المكلفة بتلقي ودراسة والبت في طلبات الاعتماد.",
      conditionsTitle: "شروط الأهلية",
      conditions: [
        "أن يكون من جنسية مغربية (أو بتفويض من منظمة أجنبية).",
        "أن يكون بالغاً ومتمتعاً بحقوقه المدنية والسياسية.",
        "عدم تسجيل أي إدانة قضائية غير مستردة الاعتبار.",
        "ألا يكون مرشحاً أو عضواً في حزب سياسي مشارك."
      ],
      modalitesTitle: "كيفية الحصول على الاعتماد",
      modalites: [
        "إيداع طلب كتابي داخل الآجال المحددة من طرف اللجنة.",
        "التقييم وفق معايير دقيقة وشروط استقلالية وخبرة صارمة.",
        "تسليم بطاقة الملاحظة الانتخابية، وهي شخصية وغير قابلة للتفويت.",
        "التوقيع واحترام \"ميثاق الملاحظة المستقلة والمحايدة للانتخابات\"."
      ],
      section3Title: "حقوق وواجبات المراقب",
      droitsTitle: "حقوقكم في الميدان",
      droits: [
        "الولوج الحر إلى مكاتب التصويت (شريطة إبراز البطاقة).",
        "حرية التنقل داخل الدائرة الانتخابية المرخصة.",
        "الحق في الحصول على المعلومات المتعلقة بسير التصويت والنتائج.",
        "الحماية القانونية الكاملة خلال مزاولة المهام الميدانية."
      ],
      obligationsTitle: "التزاماتكم الصارمة",
      obligations: [
        "الحياد التام: التحلي بموضوعية واستقلالية مطلقة.",
        "عدم التدخل أو إبداء توجيهات لعمل رئيس مكتب التصويت.",
        "الامتناع عن الإدلاء بأي تصريحات بخصوص النتائج قبل الإغلاق التام.",
        "إعداد تقرير موضوعي ومفصل يوجه خصيصاً إلى اللجنة المعتمدة."
      ],
      cndhTitle: "اللجنة الخاصة لاعتماد ملاحظي الانتخابات",
      cndhBtn: "زيارة المنصة الرسمية",
      downloadTitle: "تحميل الاستمارات الخاصة بك",
      downloadDesc: "بقرار من اللجنة، توضع رهن إشارة العموم استمارات طلب الاعتماد وكذا ميثاق الملاحظة عبر البوابة الإلكترونية.",
      suggestedTitle: "قد يهمك أيضاً:",
      sugVoterTitle: "التحقق من التسجيل",
      sugVoterDesc: "تأكد من وجود اسمك في اللوائح لتتمكن من التصويت يوم الاقتراع.",
      sugQuizTitle: "اختبار التوجه السياسي",
      sugQuizDesc: "اكتشف الحزب السياسي الأقرب إلى قناعاتك.",
      sugPartiTitle: "المشاركة السياسية",
      sugPartiDesc: "تعرف على كيفية الانضمام لحزب أو تقديم ترشيحك."
    },
    expectations: {
      badge: "صوت المواطنين",
      title: "تطلعات وأولويات المواطنين",
      subtitle: "لنتشارك بذكاء جماعي: عبر عن تطلعاتك بإيجاز.",
      respondents: "مجيب",
      btnSpeak: "تعبير",
      updatedAt: "تحديث في",
      filters: { all: "الجميع", citizen: "البالغون +30", young: "الشباب -29", women: "النساء", ong: "الجمعيات" },
      kpiRespondents: "المشاركون",
      kpiUnique: "أولويات فريدة",
      kpiTop: "أعلى ذكر",
      kpiTop3: "نسبة التوب 3",
      emptyData: "لا توجد بيانات لهذا الفلتر",
      cloudTitle: "سحابة الأولويات —",
      cloudNote: "حجم الكلمات يعكس تكرار ذكرها",
      barTitle: "أهم 10 أولويات",
      mentions: "ذكر",
      frequency: "التردد",
      tableTitle: "الترتيب الكامل للأولويات",
      colRank: "#",
      colPriority: "الأولوية",
      colMentions: "عدد الذكر",
      colPct: "%",
      colPopularity: "الشعبية",
      tableNote: "أولويات مقدمة بشكل طوعي خلال التسجيل",
      btnLess: "عرض أقل",
      modalTitle: "تعبير",
      modalSubtitle: "اقتراحان لأخذهما بعين الاعتبار",
      priority1: "الأولوية رقم 1",
      priority2: "الأولوية رقم 2",
      maxWords: "(حدا أقصى 3 كلمات)",
      placeholder1: "مثال: تشغيل الشباب",
      placeholder2: "مثال: صحة في المتناول",
      reminder: "⚡ تذكير: سيتم التحقق من أولوياتك من طرف الإدارة قبل ظهورها في البارومتر العام.",
      ethicsText: "أشهد بأن هذا المنشور يحترم الميثاق الأخلاقي للمنصة (لا تشهير، لا خطاب كراهية، لا تحريض على العنف).",
      btnSubmit: "إرسال أولوياتي",
      successTitle: "شكراً على مشاركتك!",
      successDesc: "لقد تم إرسال أولوياتك وسيتم دراستها قبل نشرها.",
      btnClose: "إغلاق"
    }
  },
  zgh: {
    nav: { home: "ⴰⵙⵏⵓⴱⴳ", why: "ⵎⴰⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵜ", parties: "ⵉⵎⵓⵔⴰⵏ ⵏ ⵜⵙⵔⵜⵉⵜ", resources: "ⵜⵉⵖⴱⵓⵍⴰ", engage: "ⵜⵜⴽⴽⵉ" },
    hero: {
      badge: "🇲🇦 ⴰⵙⴰⵢⵔⴰⵔ ⵏ ⵜⵎⵓⵔⵜ ⵏ ⵍⵎⵖⵔⵉⴱ",
      title1: "Kayn'in, ",
      titleHighlight: "ⴰⵙⴰⵢⵔⴰⵔ ⴰⵏⴰⵎⵓⵔ",
      title2: " ⵏ ⵜⴰⵙⵓⵜ ⵜⴰⵎⴰⵢⵏⵓⵜ.",
      subtitle: "ⴷⴰ, ⵏⵜⵜⵥⵕⴰ. ⵏⵜⵜⴰⴳⴳⵉ. ⵏⵜⵜⴰⵣⴰⵏ. ⵏⵜⵜⴰⵎⵓ !",
      voteBtn: "ⵔⵉⵖ ⴰⴷ ⵙⵜⵉⵖ",
      engageBtn: "ⵔⵉⵖ ⴰⴷ ⴰⵡⵙⵖ",
      observeBtn: "ⵔⵉⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵖ",
      candidatBtn: "ⵔⵉⵖ ⴰⴷ ⴳⵉⵖ ⴰⵎⵙⵜⴰⵢ",
      stats: [
        { value: "+17 ⵎⵍⵢⵓⵏ", label: "ⵉⵎⵙⵜⴰⵢⵏ ⵉⵜⵜⵓⵔⴰⵏ" },
        { value: "30%", label: "ⵜⴰⵎⵖⴰⵔⵜ ⵏ ⵜⵎⵖⴰⵔⵉⵏ" },
        { value: "34", label: "ⵉⵎⵓⵔⴰⵏ ⵉⵙⵔⵜⴰⵏⵏ" },
        { value: "395", label: "ⵉⵙⵓⵍⵏ ⴳ ⵓⴱⵕⵍⴰⵎⴰⵏ" },
      ],
    },
    why: {
      tag: "ⵎⴰⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵜ",
      title1: "ⴰⵜⵜⴽⴽⵉ ⵏⵏⴽ ⵉⴳⴰ ",
      titleHighlight: "ⴰⵎⵢⴰⵣ",
      description: "ⴰⵜⵜⴽⴽⵉ ⴰⵙⵔⵜⴰⵏ ⵓⵔ ⵉⴳⵉ ⵖⴰⵔ ⵜⴰⵎⴰⵙⵜ, ⵉⴳⴰ ⵜⴰⴼⵔⵉⵙⵜ ⵏ ⵓⵙⵏⴼⵍ.",
      reasons: [
        { title: "ⵜⵉⵎⵜⵜⵉⵜ ⵏ ⵜⵎⵙⵎⵓⵏⵜ ⵏⵏⴽ", description: "ⵜⵉⵎⵖⴰⵔⵉⵏ ⴷ ⵉⵄⵔⵔⵉⵎⵏ ⴳⴰⵏ ⵓⴳⴳⴰⵔ ⵏ 60% ⵏ ⵉⵎⵣⴷⴰⵖⵏ." },
        { title: "ⴰⵎⵃⵉⴹ ⵏ ⵉⵣⵔⴼⴰⵏ ⵏⵏⴽ", description: "ⴰⵙⵜⴰⵢ ⵉⴳⴰ ⴰⵣⵔⴼ ⴰⴷⵓⵙⵜⵓⵔⴰⵏ." },
        { title: "ⴰⵙⵏⴼⵍ ⵉ ⵓⵣⴽⴽⴰ", description: "ⴰⵡⵉ ⵜⵉⵖⴰⵡⵙⵉⵡⵉⵏ ⵜⵉⵎⴰⵢⵏⵓⵜⵉⵏ ⵉ ⵜⵎⵔⵙⴰⵍ ⵜⵉⵙⵔⵜⴰⵏⵉⵏ." },
        { title: "ⴰⵙⵉⴷⴷ ⵏ ⵜⵎⴰⴳⵔⴰⵡⵜ", description: "ⴽⵓ ⵜⴰⵖⴻⵛⵜ ⵜⴳⴰ ⴰⵙⵙⴰⵖ." },
      ],
      pageTitle: "ⵎⴰⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵜ ⴳ ⵜⵓⴷⵔⵜ ⵜⴰⴳⴷⵓⴷⴰⵏⵜ?",
      pageSubtitle: "ⵙⵙⵏ ⵜⴰⵖⴱⵓⵍⵜ ⵏ ⵓⵜⵜⴽⴽⵉ ⵏⵏⴽ ⴰⵎⴰⴹⵍⴰⵏ.",
      detailedReasons: [
        { title: "🗳️ ⵜⴰⵖⴻⵛⵜ ⵏⵏⴽ ⴷ ⵜⴰⵥⴹⴰⵕⵜ ⵏⵏⴽ", description: "ⴰⵙⵜⴰⵢ ⵉⴳⴰ ⴰⴱⵔⵉⴷ ⵓⵙⵔⵉⴷ ⵏ ⵓⵙⵏⴼⵍ.",
          details: ["ⴰⴷⵓⵙⵜⵓⵔ ⵏ 2011 ⵉⴹⵎⵏ ⴰⵣⵔⴼ ⵏ ⵓⵙⵜⴰⵢ", "ⵉⵙⵜⴰⵢⵏ ⵉⵊⵎⴰⵄⵉⵢⵏ ⵜⵜⵉⵏⵉⵏ ⴰⵙⵙⵓⴷⵙ ⵏ ⵜⵖⵔⵎⵜ ⵏⵏⴽ", "ⵉⵙⵜⴰⵢⵏ ⵉⵊⵉⵀⴰⵡⵉⵢⵏ ⵜⵜⵉⵏⵉⵏ ⵜⵉⵎⵙⴰⵔⵉⵏ ⵏ ⵜⵏⴼⵍⵉⵜ"] },
        { title: "👩 ⵜⵉⵎⵖⴰⵔⵉⵏ ⴳ ⵡⴰⵎⵎⴰⵙ ⵏ ⵓⵙⵏⴼⵍ", description: "ⴰⵜⵜⴽⴽⵉ ⵏ ⵜⵎⵖⴰⵔⵉⵏ ⵉⴳⴰ ⴰⵎⵇⵇⵔⴰⵏ.",
          details: ["ⵜⴰⵎⵖⴰⵔⵜ ⵏ 30% ⵜⴹⵎⵏ ⵜⵉⵎⵜⵜⵉⵜ ⵏ ⵜⵎⵖⴰⵔⵉⵏ", "ⵜⵉⵎⵖⴰⵔⵉⵏ ⴳⴰⵏⵜ 51% ⵏ ⵉⵎⵣⴷⴰⵖⵏ"] },
        { title: "🧑‍🎓 ⵉⵄⵔⵔⵉⵎⵏ, ⴰⵎⵓⵜⵓⵔ ⵏ ⵓⵣⴽⴽⴰ", description: "ⵓⴳⴳⴰⵔ ⵏ 40% ⵏ ⵉⵎⵣⴷⴰⵖⵏ ⴷⴷⴰⵡ ⵏ 25 ⵏ ⵓⵙⴳⴳⵯⴰⵙ.",
          details: ["ⴰⵙⴳⴳⵯⴰⵙ ⴰⵎⵥⵢⴰⵏ ⵏ ⵓⵙⵜⴰⵢ: 18", "ⵉⵄⵔⵔⵉⵎⵏ ⴰⵡⵉⵏ ⵜⵉⵖⴰⵡⵙⵉⵡⵉⵏ ⵜⵉⵎⴰⵢⵏⵓⵜⵉⵏ"] },
        { title: "🏛️ ⴰⵙⵉⴷⴷ ⵏ ⵜⵎⵔⵙⴰⵍ ⵜⵉⵎⴰⴳⵔⴰⵡⵉⵏ", description: "ⴰⵜⵜⴽⴽⵉ ⴰⵅⴰⵜⴰⵔ ⵉⵙⵙⵉⴷⴷ ⵜⴰⵎⴰⴳⵔⴰⵡⵜ.",
          details: ["ⴰⵏⵏⵛⵜ ⴰⵅⴰⵜⴰⵔ ⵏ ⵓⵜⵜⴽⴽⵉ ⵉⵙⵙⵉⴷⴷ ⵜⴰⵎⴰⵙⴰⵢⵜ", "ⴽⵓ ⵜⴰⵎⵏⴰⴹⵜ ⵜⵙⵜⵀⵍ ⵜⵉⵎⵜⵜⵉⵜ"] },
      ],
    },
    parties: {
      tag: "ⵉⵎⵓⵔⴰⵏ ⵏ ⵜⵙⵔⵜⵉⵜ",
      title1: "ⵉⵙⵙⵏ ",
      titleHighlight: "ⵉⵖⴰⵡⴰⵙⵏ",
      description: "ⵉⵙⵙⵏ ⵅⴼ ⵉⵎⵓⵔⴰⵏ ⵉⵙⵔⵜⴰⵏⵏ ⵉⵎⵖⵔⴰⴱⵉⵢⵏ ⵉⵍⵍⴰⵏ ⴳ ⵓⴱⵕⵍⴰⵎⴰⵏ.",
      seats: "ⵉⵙⵓⵍⵏ",
      viewProgram: "ⴰⵖⴰⵡⴰⵙ",
      visitWebsite: "ⴰⵙⵉⵜ",
      otherPartiesTitle: "ⵉⵎⵓⵔⴰⵏ ⵢⴰⴹⵏ ⵉⵜⵜⵓⵎⵜⵜⵉⵏ ⴳ ⵓⴱⵕⵍⴰⵎⴰⵏ",
      otherPartiesDescription: "ⵉⵎⵓⵔⴰⵏ ⴰⴷ ⵖⵓⵔⵙⵏ ⴰⵡⴷ ⵉⵙⵓⵍⵏ ⴳ ⵓⴱⵕⵍⴰⵎⴰⵏ.",
      nonParliamentaryTitle: "ⵉⵎⵓⵔⴰⵏ ⵉⵙⵔⵜⴰⵏⵏ ⵉⵎⵖⵔⴰⴱⵉⵢⵏ ⵢⴰⴹⵏ",
      nonParliamentaryDescription: "ⵉⵎⵓⵔⴰⵏ ⵉⵜⵜⵓⵙⴽⴰⵔⵏ ⵙ ⵓⵣⵔⴼ ⵓⵔ ⵉⵜⵜⵓⵎⵜⵜⵉⵏ ⴳ ⵓⴱⵕⵍⴰⵎⴰⵏ.",
      partiesFocus: allPartiesFocus_zgh,
      partiesNames: allPartiesNames_zgh,
    },
    actions: {
      tag: "ⵜⵉⴳⴰⵡⵉⵏ ⵏ ⵜⵎⵓⵔⵜ",
      title1: "ⵜⵜⴽⴽⵉⵜ ⴳ ",
      titleHighlight: "ⵜⵉⴳⴰⵡⵉⵏ ⵏ ⵜⵎⵓⵔⵜ",
      description: "ⵜⵜⴽⴽⵉⵜ ⴳ ⵜⵓⴷⵔⵜ ⵜⴰⴳⴷⵓⴷⴰⵏⵜ ⵙ ⵜⵄⵔⵔⵉⴹⵉⵏ ⴷ ⵜⵎⵙⵍⴰⵢⵉⵏ ⴷ ⵜⵙⵎⵖⵓⵔⵉⵏ.",
      usefulLinks: "ⵉⵣⴷⴰⵢⵏ ⵉⵎⵙⴷⴰⵡⵏ",
      items: [
        { title: "ⵜⵉⵄⵔⵔⵉⴹⵉⵏ ⵏ ⵜⵎⵓⵔⵜ", description: "ⵙⴳⵎⴹⵜ ⵜⵉⵄⵔⵔⵉⴹⵉⵏ ⴰⴷ ⵜⴰⵡⵉⵜ ⵜⵓⵜⵔⵉⵡⵉⵏ ⵏⵏⴽ.", cta: "ⵉⵙⵙⵏ ⵓⴳⴳⴰⵔ", slug: "petitions",
          content: ["ⴰⴷⵓⵙⵜⵓⵔ ⵏ 2011 ⵉⴹⵎⵏ ⴰⵣⵔⴼ ⵏ ⵜⵄⵔⵔⵉⴹⵜ."], links: [{ label: "ⴰⴱⵕⵍⴰⵎⴰⵏ", url: "https://www.chambredesrepresentants.ma" }] },
        { title: "ⵜⵉⵎⵙⵍⴰⵢⵉⵏ ⴷ ⵜⵏⵥⵉⵡⵉⵏ", description: "ⵙⵉⴷⴼⵜ ⵜⵉⵎⵙⵍⴰⵢⵉⵏ ⵏⵏⴽ ⵉ ⵜⵎⵔⵙⴰⵍ.", cta: "ⵙⵉⴷⴼ ⵜⴰⵏⵥⵉⵡⵜ", slug: "doleances",
          content: ["ⴰⵎⵙⵖⵉⵡⵙ ⵏ ⵜⴳⵍⴷⵉⵜ ⵉⵜⵜⴰⵔⴰ ⵜⵉⵎⵙⵍⴰⵢⵉⵏ ⵏ ⵉⵎⵖⵔⴰⴱⵉⵢⵏ."], links: [{ label: "ⵛⵉⴽⴰⵢⴰ", url: "https://www.chikaya.ma" }] },
        { title: "ⵜⵉⵙⵎⵖⵓⵔⵉⵏ ⵏ ⵜⵎⵓⵔⵜ", description: "ⵜⵜⴽⴽⵉⵜ ⴳ ⵜⵙⵎⵖⵓⵔⵉⵏ ⵅⴼ ⵉⵣⵔⴼⴰⵏ ⴷ ⵜⴰⵏⴱⴰⴹⵜ.", cta: "ⵉⵙⵙⵏ ⵜⵉⵙⵎⵖⵓⵔⵉⵏ", slug: "formations",
          content: ["ⵜⵜⵓⵙⵙⵓⴷⵙⵏⵜ ⵜⵙⵎⵖⵓⵔⵉⵏ ⵅⴼ ⵜⵎⵓⵔⵜ ⵜⴰⵎⵙⴽⴰⵔⵜ."], links: [{ label: "ⵜⴰⵎⴱⴰⴹⵜ ⵏ ⵜⵏⴼⵍⵉⵜ ⵜⴰⵏⴼⴳⴰⵏⵜ", url: "https://www.indh.ma" }] },
        { title: "ⵜⵉⵡⵓⵔⵉⵡⵉⵏ ⵏ ⵜⵎⵛⴰⵡⴰⵔⵜ", description: "ⵎⵓⵏⵜ ⴷ ⵜⵡⵓⵔⵉⵡⵉⵏ ⵏ ⵜⵎⵛⴰⵡⴰⵔⵜ.", cta: "ⵜⵜⴽⴽⵉ ⴳ ⵜⵡⵓⵔⵉⵡⵉⵏ", slug: "ateliers",
          content: ["ⵜⵉⵡⵓⵔⵉⵡⵉⵏ ⵏ ⵜⵎⵛⴰⵡⴰⵔⵜ ⵜⵜⴰⵊⵊⴰⵏⵜ ⵉⵎⵖⵔⴰⴱⵉⵢⵏ ⴰⴷ ⵜⵜⴽⴽⵉⵏ."], links: [{ label: "ⵜⵉⵖⵔⵎⵉⵡⵉⵏ ⵜⵉⵏⴰⵎⵓⵔⵉⵏ", url: "https://www.collectivites-territoriales.gov.ma" }] },
        { title: "ⵉⵡⵔⴰⵛⵏ ⵏ ⵜⵎⵓⵔⵜ", description: "ⵜⵜⴽⴽⵉⵜ ⴳ ⵉⵡⵔⴰⵛⵏ ⵏ ⵜⵏⴼⵍⵉⵜ.", cta: "ⵎⵓⵏ ⴷ ⵓⵡⵔⴰⵛ", slug: "chantiers",
          content: ["ⵉⵡⵔⴰⵛⵏ ⵏ ⵜⵎⵓⵔⵜ ⵜⵜⵎⵓⵏⵏ ⵉⵄⵔⵔⵉⵎⵏ ⴷ ⵜⵎⵙⵎⵓⵏⵉⵏ."], links: [{ label: "ⵜⴰⵎⴱⴰⴹⵜ ⵏ ⵜⵏⴼⵍⵉⵜ ⵜⴰⵏⴼⴳⴰⵏⵜ", url: "https://www.indh.ma" }] },
      ],
    },
    resources: {
      tag: "ⵜⵉⵖⴱⵓⵍⴰ",
      title1: "ⴰⵢⵏⵏⴰ ⵉⵅⵚⵚⴰ ⴰⴷ ",
      titleHighlight: "ⵜⵙⵙⵏⵜ",
      description: "ⵉⵎⵔⵙⵉⵏ ⵉⵎⵙⴷⴰⵡⵏ ⴰⴽ ⵉⵎⵓⵏⵏ ⴳ ⵓⴱⵔⵉⴷ ⵏⵏⴽ ⴰⵎⴰⴹⵍⴰⵏ.",
      items: [
        { title: "ⴰⵙⵖⵍ ⴳ ⵜⵍⴳⴰⵎⵉⵏ ⵏ ⵓⵙⵜⴰⵢ", description: "ⴰⵎⵔⵙⵉ ⵉ ⵓⵙⵖⵍ ⴷ ⵓⵙⴼⵇⵇⵔ.", cta: "ⵖⵔ ⴰⵎⵔⵙⵉ", slug: "inscription",
          content: ["ⴰⵙⵖⵍ ⴳ ⵜⵍⴳⴰⵎⵉⵏ ⵏ ⵓⵙⵜⴰⵢ ⵉⴳⴰ ⴰⵎⵇⵇⵔⴰⵏ.", "ⴰⵣⵎⵣ ⵏ ⵓⵙⵖⵍ ⵉⵕⵥⵎ ⵙⴳ 15 ⵎⴰⵢ ⴰⵔ 13 ⵢⵓⵏⵢⵓ 2026 (ⴰⴹⴰⵀⵉⵔ 690.26)."],
          links: [{ label: "ⴰⵙⴼⵇⵇⵔ ⵏ ⵓⵙⵖⵍ ⵏⵏⴽ", url: "https://www.listeselectorales.ma/fr/Recherche_BV_f.aspx" }] },
        { title: "ⴰⵙⵓⵜⵔ: ⴰⵎⵔⵙⵉ ⴰⵎⵙⴷⴰⵡ", description: "ⵜⵉⵡⵉⵍⴰ ⵏ ⵓⵙⵢⴰⴼ ⴷ ⵜⵎⵀⴰⵍⵜ.", cta: "ⵉⵙⵙⵏ ⵓⴳⴳⴰⵔ", slug: "candidature",
          content: ["ⵉⵅⵚⵚⴰ ⴰⴷ ⵜⴳⵜ ⴰⵎⵖⵔⴰⴱⵉ ⴷ ⴰⴷ ⵜⵜⵓⵔⴰⵜ."],
          links: [{ label: "ⴰⴱⵕⵍⴰⵎⴰⵏ", url: "https://www.chambredesrepresentants.ma" }] },
        { title: "ⴰⴼ ⴰⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ ⵏⵏⴽ", description: "ⴰⴼ ⴰⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ ⵉⵜⵜⵓⵣⵏⵏ ⵉⴽ.", cta: "ⴰⴼ ⴰⵙⵉⵔⴰ", slug: "bureau-vote",
          content: ["ⴽⵓ ⴰⵎⵙⵜⴰⵢ ⵉⵜⵜⵓⵣⵏ ⵉ ⵓⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ."],
          links: [{ label: "ⴰⵔⵣⵣⵓ ⵏ ⵓⵙⵉⵔⴰ", url: "https://www.listeselectorales.ma/fr/Recherche_BV_f.aspx" }] },
        { title: "ⴰⵙⵎⵍⵓⵙⵙⴰⵏ ⵏ ⵓⵙⵜⴰⵢ 2026", description: "ⵜⵉⵙⴽⵉⵍⵉⵏ ⵜⵉⵎⵇⵇⵔⴰⵏⵉⵏ.", cta: "ⵥⵕ ⵜⵉⵙⴽⵉⵍⵉⵏ", slug: "calendrier",
          content: ["ⴰⵙⵜⴰⵢ ⴰⵎⴰⵜⴰⵢ ⵉⵜⵜⵓⵙⴽⴰⵔ ⴳ 23 ⵛⵓⵜⴰⵏⴱⵉⵔ 2026.", "ⵉⵙⵜⴰⵢⵏ ⵜⵜⵓⵙⵙⵓⴷⵙⵏ ⵙ ⵓⵙⵎⵍⵓⵙⵙⴰⵏ."],
          links: [{ label: "ⴰⴱⵕⵍⴰⵎⴰⵏ", url: "https://www.chambredesrepresentants.ma" }] },
        { title: "ⵜⴰⵎⵙⴰⵙⵜ ⵏ ⵜⵎⵓⵔⵜ", description: "ⵥⵕⵜ ⵜⵉⵎⵙⴰⵙⵉⵏ ⵏ ⵜⵎⵓⵔⵜ ⵏ ⵓⵡⴰⵏⴽ ⴷ ⵜⵎⵏⴰⴹⵉⵏ ⴷ ⵜⵖⵉⵡⴰⵏⵉⵏ.", cta: "ⵥⵕ ⵜⵉⵎⵙⴰⵙⵉⵏ", slug: "budget-citoyen",
          content: ["ⵜⴰⵎⵙⴰⵙⵜ ⵏ ⵜⵎⵓⵔⵜ ⵜⴳⴰ ⴰⵙⵎⵏⵉⴷ ⵉⵙⵙⵀⵍⵏ ⵏ ⵜⵎⵙⴰⵙⵜ ⵏ ⵓⵡⴰⵏⴽ.", "ⵜⴰⵎⴰⵡⴰⵙⵜ ⵏ ⵜⴷⵎⵙⴰ ⵜⵙⵓⴼⵓⵖ ⵜⴰⵎⵙⴰⵙⵜ ⵏ ⵜⵎⵓⵔⵜ ⴽⵓ ⴰⵙⴳⴳⵯⴰⵙ."],
          links: [{ label: "ⵜⴰⵎⵙⴰⵙⵜ ⵏ ⵜⵎⵓⵔⵜ - ⵜⴰⵎⴰⵡⴰⵙⵜ ⵏ ⵜⴷⵎⵙⴰ", url: "https://www.finances.gov.ma/fr/Pages/budget-citoyen.aspx" }, { label: "ⵜⵉⵙⴼⵔⴰⵏ ⵏ ⵓⵎⵃⴰⵙⴰⴱ", url: "https://www.courdescomptes.ma" }] },
        { title: "ⵜⴰⵎⵙⴰⵙⵜ ⵜⴰⵎⵙⵉⵙⴰⵏⵜ ⵏ ⵓⵏⴰⵡ", description: "ⵜⵉⵙⵓⴼⴰⵖ ⴷ ⵜⵎⵙⴰⵙⵉⵏ ⵏ ⵓⵏⴰⵡ ⴰⵏⴰⵎⵓⵏ.", cta: "ⵥⵕ BSG", slug: "bsg",
          content: ["ⵜⴰⵎⵙⴰⵙⵜ ⵜⴰⵎⵙⵉⵙⴰⵏⵜ ⵏ ⵓⵏⴰⵡ ⵜⴳⴰ ⵜⴰⵎⵇⵇⵔⴰⵏⵜ ⴳ ⵍⵎⵖⵔⵉⴱ.", "ⵍⵎⵖⵔⵉⴱ ⵉⴳⴰ ⴰⵎⵣⵡⴰⵔⵓ ⴳ ⵉⴼⵔⵉⵇⵢⴰ ⴳ ⵜⵎⵙⴰⵙⵜ ⵏ ⵓⵏⴰⵡ."],
          links: [{ label: "ⴰⵏⴻⵇⵇⵉⵙ ⵏ ⵓⵏⴰⵡ - ⵜⴰⵎⴰⵡⴰⵙⵜ ⵏ ⵜⴷⵎⵙⴰ", url: "https://www.finances.gov.ma/fr/Pages/rapport-genre.aspx" }, { label: "ⵜⴰⵎⵓⵏⵜ ⵏ ⵜⵎⵖⴰⵔⵉⵏ ⵏ ⵜⵎⵜⵜⴰ ⵉⵎⵓⵏⵏ", url: "https://morocco.unwomen.org" }] },
      ],
    },
    cta: {
      title1: "ⴰⵙⵏⴼⵍ ⵉⵜⵜⴰⵣⵏ ⵙ ",
      titleHighlight: "ⴽⵢⵢⵉ",
      description: "ⵎⵓⵏ ⴷ ⵉⴳⵎⴰⵎⵏ ⵏ ⵉⵎⵖⵔⴰⴱⵉⵢⵏ ⵉⵜⵜⴽⴽⵉⵏ.",
      btn1: "ⵔⵉⵖ ⴰⴷ ⵙⵜⵉⵖ",
      btn2: "ⵔⵉⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵖ",
    },
    footer: {
      description: "ⴰⵙⴰⵢⵔⴰⵔ ⵏ ⵜⵎⵓⵔⵜ ⵉ ⵓⵙⵉⴷⴷ ⵏ ⵓⵜⵜⴽⴽⵉ ⵏ ⵜⵎⵖⴰⵔⵉⵏ ⴷ ⵉⵄⵔⵔⵉⵎⵏ.",
      usefulLinks: "ⵉⵣⴷⴰⵢⵏ ⵉⵎⵙⴷⴰⵡⵏ",
      links: ["ⴰⵙⵖⵍ ⴰⵏⵙⵜⴰⵢ", "ⴰⵎⵔⵙⵉ ⵏ ⵓⵎⵙⵓⵜⵔ", "ⵉⵎⵓⵔⴰⵏ ⵉⵙⵔⵜⴰⵏⵏ", "ⵜⵉⴳⴰⵡⵉⵏ ⵏ ⵜⵎⵓⵔⵜ", "ⴰⵎⴰⵏⴰⵢ ⴰⵏⵙⵜⴰⵢ"],
      contact: "ⴰⵎⵢⴰⵡⴰⴹ",
      copyright: "© 2026 ⵎⵛⴰⵔⴰⴽⴰ. ⴰⵣⵔⴼ ⵉⵜⵜⵓⵃⵉⴹⵏ.",
    },
    voter: {
      title: "ⵙⴼⵇⵇⵔ ⴰⵙⵖⵍ ⵏⵏⴽ ⴰⵏⵙⵜⴰⵢ",
      subtitle: "ⵙⴼⵇⵇⵔ ⵉⵙ ⵜⵜⵓⵔⴰⵜ ⴳ ⵜⵍⴳⴰⵎⵉⵏ ⵏ ⵓⵙⵜⴰⵢ.",
      checkTitle: "ⴰⵙⴼⵇⵇⵔ ⵏ ⵓⵙⵖⵍ",
      checkDescription: "ⵙⵎⵔⵙ ⴰⵙⴰⵢⵔⴰⵔ ⵓⵏⵚⵉⴱ ⵏ ⵜⵍⴳⴰⵎⵉⵏ ⵏ ⵓⵙⵜⴰⵢ.",
      checkBtn: "ⵙⴼⵇⵇⵔ ⴰⵙⵖⵍ ⵉⵏⵓ",
      findTitle: "ⴰⴼ ⴰⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ ⵏⵏⴽ",
      findDescription: "ⵙⵜⵉ ⴰⵏⵙⴰ ⵏⵏⴽ ⴰⴷ ⵜⴰⴼⵜ ⴰⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ.",
      region: "ⵜⴰⵎⵏⴰⴹⵜ",
      province: "ⵜⴰⵙⴳⴰ / ⵜⴰⵎⵏⴱⴰⴹⵜ",
      commune: "ⵜⴰⵖⵉⵡⴰⵏⵜ",
      bureau: "ⴰⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ",
      searchBtn: "ⵔⵣⵓ",
    },
    engagement: {
      title: "ⵔⵉⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵖ",
      subtitle: "ⵎⵢⴰⵡⴰⴹ ⴷ ⵓⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ ⴰⴷ ⵜⵎⵍⵜ ⴰⵜⵜⴽⴽⵉ ⵏⵏⴽ.",
      party: "ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ",
      selectParty: "ⵙⵜⵉ ⴰⵎⵓⵔ",
      object: "ⴰⵙⵏⵜⵍ",
      firstName: "ⵉⵙⵎ",
      lastName: "ⴰⵙⵙⴰⵖ ⵏ ⵜⵡⵊⴰ",
      genre: "ⴰⵏⴰⵡ",
      selectGenre: "ⵙⵜⵉ ⴰⵏⴰⵡ",
      genderOptions: { male: "ⴰⵔⴳⴰⵣ", female: "ⵜⴰⵎⵖⴰⵔⵜ" },
      age: "ⴰⵡⵜⴰⵢ",
      selectAge: "ⵙⵜⵉ ⴰⵡⵜⴰⵢ ⵏⵏⴽ",
      ageOptions: { "18-24": "18-24 ⵉⵙⴳⴳⵯⴰⵙⵏ", "25-34": "25-34 ⵉⵙⴳⴳⵯⴰⵙⵏ", "35-44": "35-44 ⵉⵙⴳⴳⵯⴰⵙⵏ", "45-54": "45-54 ⵉⵙⴳⴳⵯⴰⵙⵏ", "55-64": "55-64 ⵉⵙⴳⴳⵯⴰⵙⵏ", "65+": "65 ⵉⵙⴳⴳⵯⴰⵙⵏ ⴷ ⵓⴳⴳⴰⵔ" },
      profession: "ⵜⴰⵡⵡⵓⵔⵉ",
      selectProfession: "ⵙⵜⵉ ⵜⴰⵡⵡⵓⵔⵉ ⵏⵏⴽ",
      region: "ⵜⴰⵎⵏⴰⴹⵜ",
      province: "ⵜⴰⵙⴳⴰ / ⵜⴰⵎⵏⴱⴰⴹⵜ",
      commune: "ⵜⴰⵖⵉⵡⴰⵏⵜ",
      phone: "ⴰⵜⵉⵍⵉⴼⵓⵏ",
      email: "ⵉⵎⴰⵢⵍ",
      message: "ⵜⴰⴱⵔⴰⵜ",
      declaration: "ⵇⴱⵍⵖ ⴰⴷ ⵜⵜⵡⴰⵙⵎⵔⵙⵏ ⵉⵏⵖⵎⵉⵙⵏ ⵏ ⵜⴼⵔⴽⵉⵜ ⴰⴷ ⵙⵖ ⴷⴰⵔ ⵓⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ ⴰⴼⴰⴷ ⴰⴷ ⵉⵙⵡⵓⴷⴷⵓ ⵜⵓⵜⵔⴰ ⵉⵏⵓ.",
      cndpDisclaimer: "",
      submit: "ⴰⵣⵏ ⵜⵓⵜⵔⴰ ⵉⵏⵓ",
      successTitle: "ⵉⵜⵜⵓⴰⵣⵏ!",
      successMessage: "ⵜⴰⴱⵔⴰⵜ ⵏⵏⴽ ⵜⵜⵓⵔⴰ. ⴷⴼⴼⵉⵔ ⵏ ⵓⵙⴼⵇⵇⵔ, ⴰⴷ ⵜⵜⵓⴰⵣⵏ ⵉ ⵓⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ.",
      errors: { required: "ⵉⴳⵔ ⴰⴷ ⵉⵅⵚⵚⴰ", email: "ⵉⵎⴰⵢⵍ ⵓⵔ ⵉⵖⵉ", phone: "ⴰⵜⵉⵍⵉⴼⵓⵏ ⵓⵔ ⵉⵖⵉ" },
    },
    common: { backHome: "ⵓⵖⴰⵍ ⵙ ⵜⵙⵏⴰ ⵜⴰⵎⵣⵡⴰⵔⵓⵜ", loading: "ⴰⵙⵔⴰⴳ..." },
    candidacy: {
      title: "ⵉⵙⵜⴰⵢⵏ 2026 : ⴰⴱⵔⵉⴷ ⵏ",
      titleHighlight: "ⵓⵎⵙⵜⴰⵢ",
      subtitle: "ⵙⵙⵏ ⵜⵉⴼⴰⴹⵉⵡⵉⵏ, ⵜⵉⵎⵓⵣⴰⵔ, ⴷ ⵓⵙⴷⵔⵉⵎ ⵉ ⵓⵙⵜⴰⵢ ⴳ ⵍⵎⵖⵔⵉⴱ.",
      btnParcours: "ⴰⴱⵔⵉⴷ ⵏ ⵓⵙⵜⴰⵢ",
      plafondsCard: {
        label: "ⴰⵡⵜⵜⵓ ⴰⵣⵔⴼⴰⵏ ⴰⵎⵇⵇⵔⴰⵏ",
        amount: "600.000",
        currency: "ⴷⵔⵀⵎ"
      },
      tabGeneral: "ⴰⴱⵔⵉⴷ ⴰⵎⴰⵜⴰⵢ",
      tabYouth: "ⵉⵄⵔⵔⵉⵎⵏ -35 ⵏ ⵓⵙⴳⴳⵯⴰⵙ",
      eligibilityTitle: "ⵜⵉⴼⴰⴹⵉⵡⵉⵏ ⵏ ⵓⵙⵜⴰⵢ",
      eligibilityItems: [
        { title: "ⵜⴰⵏⴰⵎⵓⵔⵜ", desc: "ⴰⴷ ⵜⵉⵍⵉⵜ ⵜⴰⵏⴰⵎⵓⵔⵜ ⵜⴰⵎⵖⵔⴰⴱⵉⵜ ⵙ ⵓⵙⵉⴷⴷ ⵉⵇⵇⵓⵔⵏ." },
        { title: "ⴰⵡⵜⴰⵢ (ⴰⴱⵕⵍⴰⵎⴰⵏ)", desc: "ⴰⴷ ⵜⵍⴽⵎⵜ 23 ⵏ ⵓⵙⴳⴳⵯⴰⵙ ⴳ ⵡⴰⵙⵙ ⵏ ⵓⵙⵜⴰⵢ." },
        { title: "ⵉⵣⵔⴼⴰⵏ", desc: "ⴰⴷ ⵜⵜⵓⵔⴰⵜ ⴳ ⵜⵍⴳⴰⵎⵉⵏ ⵏ ⵓⵙⵜⴰⵢ, ⵜⵉⵍⵉⵜ ⵉⵣⵔⴼⴰⵏ ⵏⵏⴽ." }
      ],
      financeTitle: "ⴰⵙⴷⵔⵉⵎ ⴷ ⵓⵙⵖⴰⵡⵙⴰ",
      subventionsTitle: "ⵜⵉⵡⵉⵙⵉⵡⵉⵏ ⵏ 2026",
      subventionsDesc: "ⴰⵡⴰⵏⴽ ⵉⵙⴷⵓⵙ ⴰⵏⴰⵍ ⴰⵥⵕⴼⴰⵏ. ⵉⵜⵜⵓⵙⵖⵍⴰ ⵙ ⵚⴹⵉⵚⵜ (x6) ⵏ ⵜⵉⴽⴽⴰⵍ ⵉ ⴽⵓ ⴰⵎⴽⴰⵏ ⵉⵜⵜⵓⵔⴱⴰⵃⵏ ⵙⴳ ⵜⵎⵖⴰⵔⵜ, ⴰⵄⵔⵔⵉⵎ, ⵏⵖ ⴰⵎⵖⵔⴰⴱⵉ ⴳ ⴱⵕⵕⴰ (MRE).",
      subventionsItems: [
        "ⵜⵉⵡⵉⵙⵉ ⵜⴰⴳⴷⵓⴷⴰⵏⵜ ⵉ ⵉⵎⵓⵔⴰⵏ ⵉⵎⵇⵇⵔⴰⵏⵏ",
        "ⵓⵖⵓⵍ ⵏ ⵉⵙⴰⵔⵉⵜⵏ ⵉⵍⴽⵎ ⴰⵔ 75% ⵉ ⵉⵄⵔⵔⵉⵎⵏ"
      ],
      plafondsTitle: "ⵉⵡⵓⵜⵜⴰ ⵏ ⵓⵙⵖⴰⵡⵙⴰ ⵏ ⵉⵙⵜⴰⵢⵏ 2026",
      plafondsDesc: "ⴰⵙⵖⴰⵡⵙⴰ ⵏ ⵉⵙⵜⴰⵢⵏ ⵉⵖⴷⴷⴰ ⴰⴷ ⵉⵍⵉ ⵉⵡⵓⵜⵜⴰ ⴳ 2026 ⵙ ⵓⵎⵙⴰⵙⴰ ⴷ ⵓⴹⴰⵀⵉⵔ ⴰⵎⴰⵢⵏⵓ.",
      plafondsItems: [
        "600.000 ⴷⵔⵀⵎ ⵉ ⴽⵓⵍ ⴰⵎⵙⵜⴰⵢ",
        "800.000 ⴷⵔⵀⵎ ⵉ ⵜⵍⴳⴰⵎⵜ ⵜⴰⴷⵖⴰⵔⴰⵏⵜ",
        "1.500.000 ⴷⵔⵀⵎ ⵉ ⵜⵍⴳⴰⵎⵜ ⵜⴰⵏⵎⵏⴰⴹⵜ"
      ],
      timelineTitle: "ⴰⴱⵔⵉⴷ ⵏ ⵓⵙⵜⴰⵢ",
      timelineItems: [
        { title: "ⴰⵎⵓⵔ ⵏⵖ ⴰⵙⵉⵎⴰⵏ", desc: "ⴽⵛⵎ ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ, ⵏⵖ ⴳ ⴰⵎⵙⵜⴰⵢ ⴰⵙⵉⵎⴰⵏ ⵙ ⵓⵙⴳⵎⴹ ⵏ ⵉⵎⵙⵜⴰⵢⵏ." },
        { title: "ⵜⴰⵣⴽⵉⵜ (ⵜⴰⵣⴽⴽⵉⵜ)", desc: "ⴰⵎⵓⵔ ⵉⵜⵜⴰⴽⴽⴰ ⵜⴰⵣⴽⵉⵜ ⵉ ⵓⵎⵙⵜⴰⵢ ⴰⴼⴰⴷ ⴰⴷ ⵉⵙⵎⴷⵢⴰ ⴰⵎⵓⵔ." },
        { title: "ⴰⵙⵔⵙ ⵏ ⵜⵍⴳⴰⵎⵉⵏ", desc: "ⴰⵙⵔⵙ ⵏ ⵜⵍⴳⴰⵎⵉⵏ ⵉⵜⵜⵓⵙⴽⴰⵔ ⵙ ⵜⵖⴰⵔⴰⵙⵜ ⵜⴰⵎⵓⵟⵟⵓⵏⵜ." },
        { title: "ⴰⵎⵣⵉⵣⵣⵍ ⴰⵙⵔⵜⴰⵏ", desc: "15 ⵏ ⵡⵓⵙⵙⴰⵏ ⵏ ⵓⵎⵣⵉⵣⵣⵍ ⵙ ⵓⵙⵉⴷⴷ ⵏ HACA." }
      ],
      incompTitle: "ⵉⵡⵓⵜⵜⴰ ⵏ ⵓⵙⵜⴰⵢ (ⴰⵣⵔⴼ)",
      incompDesc: "ⵉⵏⴱⴷⴰⴷⵏ ⵓⵔ ⵖⵓⵔⵙⵏ ⴰⵣⵔⴼ ⵏ ⵓⵙⵜⴰⵢ.",
      incompNote: "* ⵓⵔ ⵉⵖⵉⵢ ⵓⵎⵙⵜⴰⵢ ⴰⴷ ⵉⴳ ⴰⵏⴱⴷⴰⴷ ⴰⴳⴷⵓⴷⴰⵏ.",
      incompItems: [
        "ⵉⵏⴱⴷⴰⴷⵏ ⵏ ⵜⵙⵏⴱⴹⴰⵢⵜ",
        "ⵉⵏⴱⴷⴰⵢⵏ ⴷ ⵉⵇⴰⴹⵉⵢⵏ",
        "ⵉⵏⴱⴷⴰⵢⵏ ⵏ ⵓⵙⵇⵇⵉⵎ ⵏ ⵜⵎⵉⴹⴰⵏ",
        "ⵉⵙⵔⴷⴰⵙⵏ ⴷ ⵉⵎⵙⵙⵓⴳⵓⵔⵏ",
        "ⵉⵡⴰⵍⵉⵢⵏ, ⵉⵄⵎⵎⴰⵍⵏ, ⵉⵇⴰⵢⴷⵏ",
        "ⵉⵏⵎⵀⴰⵍⵏ ⵏ ⵜⵎⵔⵙⴰⵍ ⵜⵉⴳⴷⵓⴷⴰⵏⵉⵏ"
      ],
      ctaTitle: "ⵡⴰⵊⴷⴷ ⴰⴷ ⵜⵜⴽⴽⵉⵜ?",
      ctaDesc: "ⴽⵛⵎ ⴰⵎⵓⵔ, ⵍⵎⴷ, ⴷ ⵜⵜⴽⴽⵉⵜ ⴳ ⵜⵎⵓⵔⵜ.",
      btnParti: "ⵙⴰⵡⵍ ⴷ ⵓⵎⵓⵔ",
      btnAgenda: "ⴰⵖⴰⵡⴰⵙ ⵏ ⵜⴳⴰⵡⵉⵏ",
      btnHome: "ⵓⵖⵓⵍ ⵖⵔ ⵓⵙⵏⵓⴱⴳ",
      youth: {
        badge: "ⴰⴹⴰⵀⵉⵔ ⴰⵎⴰⵢⵏⵓ 2.26.311",
        title: "ⴰⵏⴰⵍ ⴰⵥⵕⴼⴰⵏ ⵉ ⵉⵎⵃⵉⵣⵡⴰⵔⵏ ⴷⴷⴰⵡ 35 ⵏ ⵓⵙⴳⴳⵯⴰⵙ",
        desc: "ⵙ ⵓⵎⵙⴰⵙⴰ ⴷ ⵓⵎⴰⴳⵔⴰⴷ 23 ⵏ ⵓⵙⵍⴳⵏ ⴰⵎⴰⴷⴷⴰⵙ ⵓⵟⵟⵓⵏ 27.11 ⵉⵣⴷⵉⵏ ⴷ ⵓⵙⵇⵇⵉⵎ ⵏ ⵉⵎⵓⵔⴰ, ⴰⴹⴰⵀⵉⵔ ⴰⵎⴰⵢⵏⵓ ⵉⵙⵔⵙ ⵜⵉⵡⵓⵔⵉⵡⵉⵏ ⴷ ⵉⴱⵔⴷⴰⵏ ⵏ ⵓⵙⵙⴼⵓⵖ ⵏ ⵡⴰⵏⴰⵍ ⴰⵥⵕⴼⴰⵏ ⴰⴳⴷⵓⴷⴰⵏ ⵉ ⵜⵍⴳⴰⵎⵉⵏ ⵏ ⵓⵎⵃⵉⵣⵡⴰⵔ ⵏ ⵉⵄⵔⵔⵉⵎⵏ (ⵉⵔⴳⴰⵣⵏ ⴷ ⵜⵎⵖⴰⵔⵉⵏ) ⴷⴷⴰⵡ 35 ⵏ ⵓⵙⴳⴳⵯⴰⵙ. ⴰⵡⵜⵜⴰⵙ ⵉⴳⴰ ⵜ ⴰⴷ ⵉⵙⴷⵓⵙ ⵜⵉⵍⵉⵜ ⵏ ⵉⵄⵔⵔⵉⵎⵏ ⴳ ⵜⵎⵔⵙⵍⵜ ⵏ ⵓⵣⵔⴼ.",
        eligibilityTitle: "ⵜⵉⵡⵓⵔⵉⵡⵉⵏ ⵏ ⵓⵎⵃⵉⵣⵡⴰⵔ ⵉ ⵉⵄⵔⵔⵉⵎⵏ",
        eligibilityItems: [
          "ⴰⴷ ⵉⵍⵉⵏ ⴷⴷⴰⵡ 35 ⵏ ⵓⵙⴳⴳⵯⴰⵙ ⵉ ⵜⵍⴳⴰⵎⵉⵏ ⵜⵉⴷⵖⵔⴰⵏⵉⵏ ⵏⵖ ⵜⵉⵢⵎⵎⴰⵡⵉⵏ.",
          "ⴰⴷ ⴰⵡⵉⵏ 2% ⵏ ⵜⵖⵛⴰⵜⵉⵏ ⵃⵎⴰ ⴰⴷ ⴰⵎⵥⵏ ⴰⵏⴰⵍ.",
          "ⵉⵕⵥⵎ ⵉ ⵉⵎⵃⵉⵣⵡⴰⵔⵏ ⵏ ⵉⴽⴰⴱⴰⵕⵏ ⴷ ⵡⵉⴷ ⵓⵔ ⴹⴼⵕⵏⵉⵏ ⵉ ⴰⵡⴷ ⴰⴽⴰⴱⴰⵕ.",
          "ⴰⵣⵔⴰⴽ ⵏ ⵉⵣⵔⴼⴰⵏ ⵏ ⵉⵙⵜⴰⵢⵏ ⴳ ⵍⵎⵖⵔⵉⴱ."
        ],
        financeTitle: "ⴰⵏⴳⵔⴰⵡ ⴰⵥⵕⴼⴰⵏ ⴰⵎⴰⵥⵍⴰⵢ",
        percentAmount: "75%",
        bonusAmount: "10K",
        womensFundTitle: "ⴰⵙⵏⴷⵓⵇ ⵏ ⵡⴰⵏⴰⵍ ⵉ ⵜⵎⵙⴽⵜⵉⵜ ⵏ ⵜⵎⵖⴰⵔⵉⵏ",
        womensFundDesc: "ⵢⴰⵏ ⵓⵎⴰⵙⵙⴰⵢ ⵉⵡⴰⵜⵜⵙⵏ ⴰⴷ ⵉⵙⴷⵓⵙ ⵜⵉⵣⵎⵎⴰⵔ ⵏ ⵜⵎⵖⴰⵔⵉⵏ ⴰⴼⴰⴷ ⴰⴷ ⵉⵙⵙⵔⵅⵓ ⴰⵙⵉⴷⴼ ⵏⵏⵙⵏⵜ ⴳ ⵜⵓⴷⵔⵜ ⵜⴰⵙⵔⵜⴰⵏⵜ ⴷ ⵜⵏⵙⵜⴰⵢⵜ ⵜⴰⵏⴰⵎⵓⵔⵜ.",
        subventionsTitle: "ⴰⵏⴰⵍ ⴰⴳⴷⵓⴷⴰⵏ (ⴰⵔ 75%)",
        subventionsDesc: "ⴰⴼⴰⴷ ⴰⴷ ⵉⵔⵅⵓ ⵓⵎⵃⵉⵣⵡⴰⵔ ⵉ ⵉⵄⵔⵔⵉⵎⵏ, ⴰⵡⴰⵏⴽ ⵉⵜⵜⴰⵡⵙ ⵙ 75% ⵙⴳ 100 ⵎⵍⵢⵓⵏ ⵏ ⴷⵔⵀⵎ ⵏ 2026.",
        bonusTitle: "ⴰⵏⴰⵍ ⵉ ⵉⴽⴰⴱⴰⵕⵏ",
        bonusDesc: "ⴰⴽⴰⴱⴰⵕ ⵉⵜⵜⴰⵎⵥ 10.000 ⴷⵔⵀⵎ ⵅⴼ ⴽⵓ ⴰⵎⵃⵉⵣⵡⴰⵔ ⴰⵄⵔⵔⵉⵎ ⵉⵜⵜⵓⵙⵜⴰⵢⵏ.",
        rulesTitle: "ⵜⵉⴼⴰⵡⵜ ⴷ ⵉⵍⵓⴳⵏⵏ",
        rulesItems: [
          "ⴰⵏⴰⵍ ⵉⵜⵜⵓⵙⴽⴰⵔ ⴳ 75% ⵏ ⵡⴰⵜⵉⴳ ⴰⵎⴰⵜⴰⵢ ⵏ ⵉⵙⴰⵔⵉⵜⵏ ⵏ ⵉⵙⵜⴰⵢⵏ.",
          "ⴰⵏⴰⵍ ⵉⵜⵜⵓⵙⴽⴰⵔ ⴳ 75% ⵏ ⵉⵙⴰⵔⵉⵜⵏ ⵏⵏⴰ ⵉⵜⵜⵓⵙⴽⴰⵔⵏ ⵙ ⵜⵉⴷⵜ ⴷ ⵉⵜⵜⵓⵙⵜⴰⵢⵏ ⵙⴳ ⵓⵙⵇⵇⵉⵎ.",
          "ⵉⵎⵃⵉⵣⵡⴰⵔⵏ ⵉⵇⵇⴰⵏ ⵜⵏ ⵉⴷ ⴰⴷ ⵕⵥⵎⵏ ⴰⵎⵉⴹⴰⵏ ⵏ ⵍⴱⴰⵏⴽⴰ ⵉⵥⵍⵉⵏ ⵙ ⴽⵓ ⵜⴰⵍⴳⴰⵎⵜ.",
          "ⵉⵇⵇⴰⵏ ⴷ ⴰⴷ ⵉⵜⵜⵓⵙⴽⴰⵔ ⵓⵎⵉⴹⴰⵏ ⵏ ⵉⵙⵜⴰⵢⵏ, ⵉⵜⵜⵓⵙⵜⴰⵢⵏ ⵙⴳ ⵓⵎⵙⵉⴹⵏ ⴰⵎⴰⵥⵍⴰⵢ, ⴷ ⴰⴷ ⵉⵜⵜⵓⵙⵔⵙ ⴳ ⵓⵙⵇⵇⵉⵎ ⴰⵏⴰⴼⵍⵍⴰ ⵏ ⵉⵎⵉⴹⴰⵏⵏ."
        ]
      }
    },
    observateur: {
      heroTitle: "ⴰⴳ ⴰⵎⵥⵕⵉ ⵏ ⵓⵙⵜⴰⵢ",
      heroBadge: "ⴰⵣⵔⴼ 30-11",
      heroDesc: "ⵜⵉⵟⵟⴰⵡⵉⵏ ⵏⵏⴽ ⵉ ⵜⵎⴰⴳⵔⴰⵡⵜ! ⵙⵙⵏ ⴰⵣⵔⴼ 30-11 ⵉ ⵓⵙⵉⴷⴷ ⵏ ⵓⵙⵜⴰⵢ ⴳ ⵍⵎⵖⵔⵉⴱ.",
      definitionTitle: "ⵎⴰ ⴰⴷ ⵉⴳⴰⵏ ⵉⵏⵥⵉ ⴰⵙⵉⵎⴰⵏ ⴷ ⴰⵔⴰⵡⵙⴰⵏ ⵉ ⵡⴰⵙⵜⴰⵢⵏ?",
      definitionDesc: "ⵉⴳⴰ ⵉⵏⵥⵉ ⴰⵙⵉⵎⴰⵏ ⴷ ⴰⵔⴰⵡⵙⴰⵏ ⵉ ⵡⴰⵙⵜⴰⵢⵏ, ⵙ ⵎⴰ ⴰⴷ ⴷ ⵢⵓⵡⵉ ⵓⵙⵍⴳⵏ 30.11 ⵏⵏⴰ ⵉⵥⵍⵉⵏ ⵙ ⵜⴼⴰⴷⵉⵡⵉⵏ ⴷ ⵡⴰⵎⵎⴰⴽⵏ ⵏ ⵉⵏⵥⵉ ⴰⵙⵉⵎⴰⵏ ⴷ ⴰⵔⴰⵡⵙⴰⵏ ⵉ ⵡⴰⵙⵜⴰⵢⵏ, ⴽⵓ ⵜⴰⵎⵀⵍⵜ ⵉⵙⵡⵓⵜⵜⵙⵏ, ⵙ ⵓⴹⴼⴼⵓⵕ, ⴳ ⵓⴱⴰⵔⴰⵣ, ⵏ ⵓⵣⵔⵔⵓⵢ ⵏ ⵜⵎⵀⴰⵍ ⵜⵉⵏⵙⵜⴰⵢⵉⵏ ⴷ ⵓⵙⵎⵓⵏ ⵏ ⵜⵎⵓⵛⴰ ⵏⵏⵙⵏⵜ ⵙ ⵜⵎⵙⵖⴰⵔⴰ ⴷ ⵡⴰⵏⵏⴰⴼ ⴷ ⵜⵔⴰⵡⵙⴰⵏⵜ, ⴷ ⵓⵙⵉⵜⴳ ⵏ ⵜⵎⵙⵓⵜⴰⵍ ⵏ ⵓⵏⵎⴰⵍⴰ ⵏⵏⵙⵏⵜ ⴷ ⵓⵣⵣⴳⵉⵔ ⵏⵏⵙⵏⵜ ⴷ ⵜⴼⵙⵏⴰ ⵏ ⵓⵡⵇⵇⵔ ⵏⵏⵙⵏⵜ ⵉ ⵉⵍⵓⴳⵏ ⵉⴷⵓⵙⵜⵓⵔⵏ ⴷ ⵉⴹⵕⵉⵚⵏ ⵓⵣⵔⵉⴼⵏ ⴷ ⵉⵎⵙⵙⵓⴷⵙⵏ ⵉⵣⴷⵉⵏ ⴷ ⵡⴰⵙⵜⴰⵢⵏ ⴷ ⵉⵙⴱⴷⴰⴷⵏ ⵉⴳⵔⴰⵖⵍⴰⵏⵏ, ⵙ ⵓⵙⴽⴰⵔ ⵏ ⵜⵏⴰⴹⵉⵏ ⵙⵉⵔⵙⵏ ⵉⵣⴷⵉⵏ, ⵏⵏⴰ ⴳ ⵍⵍⴰⵏ ⵜ ⵉⵏⵥⵉⵜⵏ ⵏ ⵉⴳⵎⴰⵎⵏ ⵏⵏⴰ ⵜⵏ ⵉⵙⴽⵔⵏ, ⴷ ⴳ ⵜⵉⵣⵉ ⵏ ⵓⵏⵎⵔⵉ, ⵜⵉⵖⵍⴰⴼ ⵏⵏⵙⵏⵜ ⵏⵏⴰ ⵙⵙⵉⵡⵉⴹⵏⵜ ⵉ ⵜⵏⴱⴰⴹⵉⵏ ⵏⵏⴰ ⴳⴰⵏⵉⵏ ⵜⵉⵎⵏⵜⴰⵢⵉⵏ...",
      section1Title: "ⵜⵉⵎⵔⵙⴰⵍ ⵏ ⵓⵎⵥⵕⵉ",
      institutions: [
        { title: "ⵜⵉⵎⵔⵙⴰⵍ ⵜⵉⵏⴰⵎⵓⵔⵉⵏ", desc: "ⵜⵉⵎⵔⵙⴰⵍ ⵜⵉⵏⴰⵎⵓⵔⵉⵏ ⴰⵎ ⵓⵙⵇⵇⵉⵎ ⴰⵏⴰⵎⵓⵔ ⵏ ⵉⵣⵔⴼⴰⵏ ⵏ ⵓⴼⴳⴰⵏ." },
        { title: "ⵜⵉⵎⵙⵎⵓⵏⵉⵏ ⵜⵉⵎⵖⵔⴰⴱⵉⵢⵉⵏ", desc: "ⵜⵉⵎⵙⵎⵓⵏⵉⵏ ⵏ ⵜⵎⵜⵜⵉ ⵜⴰⵖⵔⵎⴰⵏⵜ ⵜⵉⵏⵎⵓⵔⵉⵏ." },
        { title: "ⵜⵉⵎⵙⵎⵓⵏⵉⵏ ⵜⵉⵎⴰⴹⵍⴰⵏⵉⵏ", desc: "ⵜⵉⵎⵙⵎⵓⵏⵉⵏ ⵏ ⴱⵕⵕⴰ ⵉⵍⵍⴰⵏ ⴳ ⵓⵎⵥⵕⵉ ⴰⵙⵉⵎⴰⵏ." }
      ],
      section2Title: "ⵜⴰⵙⵇⵇⵉⵎⵜ ⵏ ⵓⵙⵉⴷⴷ",
      section2Desc: "ⵜⴰⵙⵇⵇⵉⵎⵜ ⵏ CNDH ⵜⴳⴰ ⵜⴰⵎⵢⵉⵡⵏⵜ ⵉⵜⵜⴰⴳⴳⵯⴰⵏ ⵜⵓⵜⵔⵉⵡⵉⵏ ⵏ ⵓⵎⵥⵕⵉ.",
      conditionsTitle: "ⵜⵉⴼⴰⴹⵉⵡⵉⵏ",
      conditions: [
        "ⴰⴷ ⵜⵉⵍⵉⵜ ⵜⴰⵏⴰⵎⵓⵔⵜ ⵜⴰⵎⵖⵔⴰⴱⵉⵜ.",
        "ⴰⴷ ⵜⵍⴽⵎⵜ ⴰⵡⵜⴰⵢ ⴷ ⵉⵣⵔⴼⴰⵏ ⵉⵖⵔⵎⴰⵏⵏ.",
        "ⵓⵔ ⵜⵍⵉⵜ ⵜⴰⴼⴳⵓⵔⵜ ⵏ ⵜⵙⵏⴱⴹⴰⵢⵜ.",
        "ⵓⵔ ⵜⴳⵉⵜ ⴰⵎⵙⵜⴰⵢ ⵏⵖ ⴳ ⵓⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ."
      ],
      modalitesTitle: "ⵜⵉⵖⴰⵔⴰⵙⵉⵏ ⵏ ⵓⵙⵉⴷⴷ",
      modalites: [
        "ⴰⵙⵔⵙ ⵏ ⵜⵓⵜⵔⴰ ⵜⴰⵎⵉⵔⴰⵢⵜ.",
        "ⴰⵙⵉⵜⴳ ⵙ ⵜⴼⴰⴹⵉⵡⵉⵏ ⵏ ⵓⵙⵉⵎⴰⵏ.",
        "ⴰⵙⵉⴷⴷ ⵏ ⵜⴽⴰⵕⴹⴰ ⵏ ⵓⵎⵥⵕⵉ.",
        "ⴰⵙⴳⵎⴹ ⵏ ⵓⵎⵢⴰⵇⵇⴰⵏ ⵏ ⵓⵎⵥⵕⵉ."
      ],
      section3Title: "ⵉⵣⵔⴼⴰⵏ ⴷ ⵉⵡⴰⵊⵉⴱⵏ",
      droitsTitle: "ⵉⵣⵔⴼⴰⵏ ⵏⵏⴽ",
      droits: [
        "ⴰⴽⵛⵛⵓⵎ ⵖⵔ ⵓⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ.",
        "ⵜⴰⵍⴻⵍⵍⵉ ⵏ ⵓⵎⵓⵙⵙⵓ ⴳ ⵜⵏⴰⴹⵜ.",
        "ⴰⵣⵔⴼ ⵏ ⵓⵙⵉⴷⴷ ⵏ ⵉⵏⵖⵎⵉⵙⵏ ⴷ ⵜⵢⴰⴼⵓⵜⵉⵏ.",
        "ⵜⴰⵏⴼⵔⵓⵜ ⵜⴰⵣⵔⴼⴰⵏⵜ."
      ],
      obligationsTitle: "ⵉⵡⴰⵊⵉⴱⵏ ⵏⵏⴽ",
      obligations: [
        "ⴰⵙⵉⵎⴰⵏ, ⵓⵔ ⵜⵜⵉⵍⵉⵜ ⴷ ⴰⵡⴷ ⵢⴰⵏ.",
        "ⵓⵔ ⵜⵙⴽⵛⴰⵎⵜ ⴳ ⵜⵡⵓⵔⵉ ⵏ ⵓⵏⵙⵙⵉⵅⴼ ⵏ ⵓⵙⵉⵔⴰ.",
        "ⵓⵔ ⵜⵙⴰⵡⴰⵍⵜ ⵅⴼ ⵜⵢⴰⴼⵓⵜⵉⵏ.",
        "ⴰⵙⵉⴷⴷ ⵏ ⵓⵏⵇⵇⵉⵙ ⵉ ⵜⵙⵇⵇⵉⵎⵜ."
      ],
      cndhTitle: "ⵜⴰⵙⵇⵇⵉⵎⵜ ⵏ ⵓⵙⵉⴷⴷ ⵏ ⵉⵎⵥⵕⵉⵢⵏ",
      cndhBtn: "ⵔⵣⴼ ⴰⵙⵉⵜ ⴰⵎⴰⴷⴷⵓⴷ",
      downloadTitle: "ⴰⴳⵎ ⵜⵉⴼⵔⴽⵉⵡⵉⵏ ⵏⵏⴽ",
      downloadDesc: "ⵜⵉⴼⵔⴽⵉⵡⵉⵏ ⵏ ⵓⵎⵥⵕⵉ ⴷ ⵓⵎⵢⴰⵇⵇⴰⵏ ⵍⵍⴰⵏⵜ ⴳ ⵓⵙⵉⵜ.",
      suggestedTitle: "ⵉⵖⵉⵢ ⴰⴷ ⵉⵙⵉⴷⴷ:",
      sugVoterTitle: "ⴰⵙⴼⵇⵇⵔ ⵏ ⵓⵙⵖⵍ",
      sugVoterDesc: "ⵙⵙⵏ ⵎⴽ ⵜⵜⵓⵔⴰⵜ ⴳ ⵜⵍⴳⴰⵎⵉⵏ.",
      sugQuizTitle: "ⵜⴰⵣⴷⴰⵢⵜ ⵜⴰⵙⵔⵜⴰⵏⵜ",
      sugQuizDesc: "ⴰⴼ ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ ⵏⵏⴽ.",
      sugPartiTitle: "ⴰⵜⵜⴽⴽⵉ ⴰⵙⵔⵜⴰⵏ",
      sugPartiDesc: "ⵙⵙⵏ ⵎⴽ ⵜⴽⵛⵎⵜ ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ."
    },
    expectations: {
      badge: "ⴰⵡⴰⵍ ⵏ ⵉⵏⴰⵎⵓⵔⵏ",
      title: "ⴰⵙⵉⵔⵎ ⴷ ⵜⵉⵣⵡⵓⵔⴰ ⵏ ⵉⵏⴰⵎⵓⵔⵏ",
      subtitle: "ⴰⴷ ⵏⴷⵔⵓ ⵙ ⵓⵏⵍⵍⵉ ⴰⵎⴰⴳⵔⴰⵡ: ⵙⵙⵉⵡⴹ ⴰⵙⵉⵔⵎ ⵏⵏⴽ ⵙ ⵜⵖⴰⵔⴰⵙⵜ ⵉⴳⵣⵣⵓⵍⵏ.",
      respondents: "ⵉⵎⵔⴰⵔⵉⵏ",
      btnSpeak: "ⵙⴰⵡⵍ",
      updatedAt: "ⴰⵙⵉⴷⴷ ⴳ",
      filters: { all: "ⴰⴽⴽⵯ", citizen: "ⵉⵎⵇⵇⵔⴰⵏⵏ +30", young: "ⵉⵄⵔⵔⵉⵎⵏ -29", women: "ⵜⵉⵎⵖⴰⵔⵉⵏ", ong: "ⵜⵉⵎⵙⵎⵓⵏⵉⵏ" },
      kpiRespondents: "ⵉⵎⵔⴰⵔⵉⵏ",
      kpiUnique: "ⵜⵉⵎⵓⵜⵜⵓⵜⵉⵏ",
      kpiTop: "ⴰⵏⵇⵇⵉⵙ",
      kpiTop3: "Top 3",
      emptyData: "ⵓⵔ ⵉⵍⵍⵉ ⵓⵎⵍⴰⵏ ⴳ ⵓⵙⵜⴰⵢ ⴰⴷ",
      cloudTitle: "ⴰⵙⵉⴷⴷ ⵏ ⵜⵎⵓⵜⵜⵓⵜⵉⵏ —",
      cloudNote: "ⵜⴰⵙⵖⴰⵔⵜ ⵏ ⵜⴳⵓⵔⵉⵡⵉⵏ ⵜⵙⵙⵉⴷⴷ ⴰⵏⵇⵇⵉⵙ",
      barTitle: "Top 10 ⵏ ⵜⵎⵓⵜⵜⵓⵜⵉⵏ",
      mentions: "ⴰⵙⵉⴷⴷ",
      frequency: "ⴰⵙⵉⴷⴷ",
      tableTitle: "ⵜⴰⵍⴳⴰⵎⵜ ⵏ ⵜⵎⵓⵜⵜⵓⵜⵉⵏ",
      colRank: "#",
      colPriority: "ⵜⴰⵎⵓⵜⵜⵓⵜ",
      colMentions: "ⴰⵙⵉⴷⴷ",
      colPct: "%",
      colPopularity: "ⴰⵏⵇⵇⵉⵙ",
      tableNote: "ⵜⵉⵎⵓⵜⵜⵓⵜⵉⵏ ⵉⵜⵜⵓⵙⴽⴰⵔⵏ ⴳ ⵓⵙⵖⵍ",
      btnLess: "ⵥⵕ ⵉⵎⵉⴽⵇ",
      modalTitle: "ⵙⴰⵡⵍ",
      modalSubtitle: "2 ⵜⵎⵓⵜⵜⵓⵜⵉⵏ ⵉ ⵍⵎⵖⵔⵉⴱ ⵏ ⵓⵣⴽⴽⴰ",
      priority1: "ⵜⴰⵎⵓⵜⵜⵓⵜ 1",
      priority2: "ⵜⴰⵎⵓⵜⵜⵓⵜ 2",
      maxWords: "(3 ⵜⴳⵓⵔⵉⵡⵉⵏ)",
      placeholder1: "ⴰⵎⴷⵢⴰ: ⵜⴰⵡⵓⵔⵉ ⵉ ⵉⵄⵔⵔⵉⵎⵏ",
      placeholder2: "ⴰⵎⴷⵢⴰ: ⵜⴰⴷⵓⵙⵉ",
      reminder: "⚡ ⴰⵙⵉⴷⴷ: ⵜⵉⵎⵓⵜⵜⵓⵜⵉⵏ ⵏⵏⴽ ⵔⴰⴷ ⵜⵜⵓⵙⵉⴷⴷⵏⵜ.",
      ethicsText: "ⴰⵙⴳⵎⴹ: ⵓⵔ ⵉⵍⵍⵉ ⴰⵡⴰⵍ ⵉⵅⵛⵏⵏ.",
      btnSubmit: "ⴰⵣⵏ ⵜⵉⵎⵓⵜⵜⵓⵜⵉⵏ ⵉⵏⵓ",
      successTitle: "ⵜⴰⵏⵎⵎⵉⵔⵜ!",
      successDesc: "ⵜⵉⵎⵓⵜⵜⵓⵜⵉⵏ ⵏⵏⴽ ⵜⵜⵓⵣⴰⵏⵏⵜ.",
      btnClose: "ⵇⵇⵏ"
    }
  },
};
